import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Star, Users, Globe, Sparkles, Eye, Share2, Lock, Check, X, Info, ZoomIn, ZoomOut, Trophy, ChevronRight, Move, RotateCcw } from 'lucide-react';
import { skyAPI, constellationsAPI, progressAPI } from '../api';
import { toast } from 'sonner';
import api from '../api';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Stars, Text, Html } from '@react-three/drei';
import * as THREE from 'three';

// Sky layer tabs
const SKY_LAYERS = [
  { id: 'personal', name: 'My Sky', icon: Star, color: '#818CF8', description: 'Stars from your learning journey' },
  { id: 'family', name: 'Family Sky', icon: Users, color: '#34D399', description: 'Constellations from your family' },
  { id: 'community', name: 'Temple Sky', icon: Globe, color: '#FBBF24', description: 'Constellations from young explorers everywhere' }
];

// Realm colors for star visualization - Use actual room IDs from backend
const REALM_COLORS = {
  memory_garden: '#4ADE80',    // Green
  radiant_will: '#FBBF24',     // Yellow/Gold
  star_map: '#60A5FA',         // Blue
  creative_forge: '#FB923C',   // Orange
  calm_cove: '#2DD4BF'         // Teal
};

const REALM_NAMES = {
  memory_garden: 'Memory Garden',
  radiant_will: 'Chamber of Radiant Will',
  star_map: 'Star Map Hall',
  creative_forge: 'Creative Forge',
  calm_cove: 'Calm Cove'
};

// ============ CONSTELLATION GOAL PATTERNS ============
// Each realm has a themed constellation pattern children work toward
// Using actual room IDs from backend
const CONSTELLATION_GOALS = {
  star_map: {
    name: 'The Explorer\'s Compass',
    story: 'Guide your path through the stars of knowledge. Each point of the compass reveals new wisdom.',
    stars: [
      { x: 50, y: 20 },  // North
      { x: 80, y: 50 },  // East
      { x: 50, y: 80 },  // South
      { x: 20, y: 50 },  // West
      { x: 50, y: 50 },  // Center
    ],
    connections: [[0, 4], [1, 4], [2, 4], [3, 4], [0, 1], [1, 2], [2, 3], [3, 0]],
    lessonsRequired: 5
  },
  radiant_will: {
    name: 'The Kindness Heart',
    story: 'A heart that grows with every act of kindness and understanding you share.',
    stars: [
      { x: 50, y: 25 },  // Top center
      { x: 30, y: 35 },  // Upper left
      { x: 70, y: 35 },  // Upper right
      { x: 20, y: 55 },  // Mid left
      { x: 80, y: 55 },  // Mid right
      { x: 35, y: 75 },  // Lower left
      { x: 65, y: 75 },  // Lower right
      { x: 50, y: 90 },  // Bottom point
    ],
    connections: [[0, 1], [0, 2], [1, 3], [2, 4], [3, 5], [4, 6], [5, 7], [6, 7]],
    lessonsRequired: 8
  },
  creative_forge: {
    name: 'The Creative Spark',
    story: 'A burst of inspiration that ignites your imagination and brings ideas to life.',
    stars: [
      { x: 50, y: 30 },  // Center top
      { x: 35, y: 45 },  // Inner left
      { x: 65, y: 45 },  // Inner right
      { x: 20, y: 35 },  // Outer left up
      { x: 80, y: 35 },  // Outer right up
      { x: 25, y: 65 },  // Outer left down
      { x: 75, y: 65 },  // Outer right down
      { x: 50, y: 75 },  // Bottom
    ],
    connections: [[0, 1], [0, 2], [1, 3], [2, 4], [1, 5], [2, 6], [5, 7], [6, 7], [3, 5], [4, 6]],
    lessonsRequired: 8
  },
  calm_cove: {
    name: 'The Peaceful Moon',
    story: 'A crescent of calm that guides you through quiet moments of reflection.',
    stars: [
      { x: 30, y: 25 },
      { x: 25, y: 40 },
      { x: 25, y: 55 },
      { x: 30, y: 70 },
      { x: 45, y: 80 },
      { x: 60, y: 85 },
      { x: 75, y: 80 },
    ],
    connections: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6]],
    lessonsRequired: 7
  },
  memory_garden: {
    name: 'The Wise Tree',
    story: 'A great tree that grows with every story and memory you collect.',
    stars: [
      { x: 50, y: 15 },  // Tree top
      { x: 35, y: 25 },  // Branch left
      { x: 65, y: 25 },  // Branch right
      { x: 40, y: 40 },  // Mid left
      { x: 60, y: 40 },  // Mid right
      { x: 50, y: 55 },  // Trunk top
      { x: 45, y: 70 },  // Trunk mid left
      { x: 55, y: 70 },  // Trunk mid right
      { x: 50, y: 85 },  // Root
    ],
    connections: [[0, 1], [0, 2], [1, 3], [2, 4], [3, 5], [4, 5], [5, 6], [5, 7], [6, 8], [7, 8]],
    lessonsRequired: 5
  }
};

// ============ 3D SKY DOME COMPONENT ============
// Interactive planetarium-style sky that can be rotated with drag controls

// Journey constellation positions on the sky dome (spherical coordinates)
// Positioned to be visible when looking around the sky
const JOURNEY_CONSTELLATIONS_3D = [
  { 
    id: 'ancient_worlds',
    name: 'Ancient Worlds',
    theta: 0, // Directly in front
    phi: Math.PI / 2.5, // Slightly above horizon - VISIBLE ON LOAD
    color: '#F59E0B',
    description: 'Explore civilizations of the past'
  },
  { 
    id: 'creativity_music',
    name: 'Creativity & Music',
    theta: Math.PI * 0.4, // Front-right
    phi: Math.PI / 2.2,
    color: '#EC4899',
    description: 'Create art and melody'
  },
  { 
    id: 'coding_logic',
    name: 'Coding Garden',
    theta: Math.PI * 0.8, // Right side
    phi: Math.PI / 2.5,
    color: '#10B981',
    description: 'Build with code'
  },
  { 
    id: 'strategy_chess',
    name: 'Strategy & Chess',
    theta: Math.PI * 1.2, // Back-right
    phi: Math.PI / 2.2,
    color: '#6366F1',
    description: 'Think and plan ahead'
  },
  { 
    id: 'nature_science',
    name: 'Nature Journey',
    theta: Math.PI * 1.6, // Back-left
    phi: Math.PI / 2.5,
    color: '#22D3EE',
    description: 'Discover the natural world'
  }
];

// Convert spherical to Cartesian coordinates on a dome
const sphericalToCartesian = (theta, phi, radius) => {
  return {
    x: radius * Math.sin(phi) * Math.cos(theta),
    y: radius * Math.cos(phi),
    z: radius * Math.sin(phi) * Math.sin(theta)
  };
};

// Mapping between 3D journey IDs and backend realm IDs
const JOURNEY_TO_REALM = {
  'ancient_worlds': 'star_map',
  'creativity_music': 'creative_forge',
  'coding_logic': 'memory_garden',
  'strategy_chess': 'radiant_will',
  'nature_science': 'calm_cove'
};

// Constellation star patterns for each journey (aligned with CONSTELLATION_GOALS)
const JOURNEY_CONSTELLATION_PATTERNS = {
  'ancient_worlds': {
    name: "Explorer's Compass",
    totalStars: 5,  // Match CONSTELLATION_GOALS.star_map
    // Star positions relative to planet center (in local space)
    stars: [
      { angle: 0, distance: 4, height: 2 },           // North
      { angle: Math.PI * 0.5, distance: 4, height: 0 },  // East
      { angle: Math.PI, distance: 4, height: -2 },       // South
      { angle: Math.PI * 1.5, distance: 4, height: 0 },  // West
      { angle: 0, distance: 2.5, height: 0 }             // Center
    ],
    // Connections between stars (indices)
    connections: [[0, 4], [1, 4], [2, 4], [3, 4], [0, 1], [1, 2], [2, 3], [3, 0]]
  },
  'creativity_music': {
    name: "Creative Spark",
    totalStars: 8,  // Match CONSTELLATION_GOALS.creative_forge
    stars: [
      { angle: 0, distance: 3, height: 2 },             // Center top
      { angle: Math.PI * 0.4, distance: 3.5, height: 1 },  // Inner left
      { angle: Math.PI * 1.6, distance: 3.5, height: 1 },  // Inner right
      { angle: Math.PI * 0.3, distance: 5, height: 1.5 },  // Outer left up
      { angle: Math.PI * 1.7, distance: 5, height: 1.5 },  // Outer right up
      { angle: Math.PI * 0.6, distance: 5, height: -1 },   // Outer left down
      { angle: Math.PI * 1.4, distance: 5, height: -1 },   // Outer right down
      { angle: Math.PI, distance: 3, height: -2 }          // Bottom
    ],
    connections: [[0, 1], [0, 2], [1, 3], [2, 4], [1, 5], [2, 6], [5, 7], [6, 7], [3, 5], [4, 6]]
  },
  'coding_logic': {
    name: "Wise Tree",
    totalStars: 5,  // Match CONSTELLATION_GOALS.memory_garden (using 5 for the core shape)
    stars: [
      { angle: 0, distance: 2.5, height: 3 },           // Tree top
      { angle: Math.PI * 0.4, distance: 4, height: 1.5 },  // Branch left
      { angle: Math.PI * 1.6, distance: 4, height: 1.5 },  // Branch right
      { angle: Math.PI * 0.5, distance: 3, height: 0 },    // Trunk left
      { angle: Math.PI * 1.5, distance: 3, height: 0 }     // Trunk right
    ],
    connections: [[0, 1], [0, 2], [1, 3], [2, 4], [3, 4]]
  },
  'strategy_chess': {
    name: "Kindness Heart",
    totalStars: 8,  // Match CONSTELLATION_GOALS.radiant_will
    stars: [
      { angle: 0, distance: 2.5, height: 2.5 },          // Top center
      { angle: Math.PI * 0.4, distance: 3.5, height: 2 },   // Upper left
      { angle: Math.PI * 1.6, distance: 3.5, height: 2 },   // Upper right
      { angle: Math.PI * 0.5, distance: 4.5, height: 0.5 }, // Mid left
      { angle: Math.PI * 1.5, distance: 4.5, height: 0.5 }, // Mid right
      { angle: Math.PI * 0.65, distance: 4, height: -1 },   // Lower left
      { angle: Math.PI * 1.35, distance: 4, height: -1 },   // Lower right
      { angle: Math.PI, distance: 2.5, height: -2.5 }       // Bottom point
    ],
    connections: [[0, 1], [0, 2], [1, 3], [2, 4], [3, 5], [4, 6], [5, 7], [6, 7]]
  },
  'nature_science': {
    name: "Peaceful Moon",
    totalStars: 7,  // Match CONSTELLATION_GOALS.calm_cove
    stars: [
      { angle: Math.PI * 0.3, distance: 4, height: 2.5 },
      { angle: Math.PI * 0.5, distance: 4.5, height: 1.5 },
      { angle: Math.PI * 0.7, distance: 5, height: 0.5 },
      { angle: Math.PI * 0.9, distance: 5, height: -0.5 },
      { angle: Math.PI * 1.1, distance: 4.5, height: -1.5 },
      { angle: Math.PI * 1.3, distance: 4, height: -2 },
      { angle: Math.PI * 1.5, distance: 3.5, height: -2.5 }
    ],
    connections: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6]]
  }
};

// Module-level cache for constellation meshes to persist across remounts
let constellationGroupCache = null;
let constellationSceneRef = null;

// Clear cache to force rebuild (call when visual changes are made)
const clearConstellationCache = () => {
  if (constellationGroupCache && constellationSceneRef) {
    constellationSceneRef.remove(constellationGroupCache);
  }
  constellationGroupCache = null;
  constellationSceneRef = null;
};

// Force clear on module load to pick up visual changes
clearConstellationCache();

// Store label positions globally for access outside Canvas
let globalLabelPositions = [];
let globalCameraRef = null;

// DOM-based labels that overlay the 3D scene
// For now, uses approximate positions based on default camera view
const ConstellationLabelsOverlay = ({ constellations, onSelectConstellation }) => {
  const [labelPositions, setLabelPositions] = useState([]);
  const animationRef = useRef(null);
  const lastUpdateRef = useRef(0);
  
  useEffect(() => {
    // Default camera: position [0, 0, 50], fov 60, looking at origin
    // Canvas dimensions assumed to be 900x500 approximately
    const defaultCamera = {
      position: new THREE.Vector3(0, 0, 50),
      fov: 60,
      aspect: 1.8, // approximate
      near: 0.1,
      far: 1000
    };
    
    // Create a projection matrix camera for calculations
    const camera = new THREE.PerspectiveCamera(defaultCamera.fov, defaultCamera.aspect, defaultCamera.near, defaultCamera.far);
    camera.position.copy(defaultCamera.position);
    camera.lookAt(0, 0, 0);
    camera.updateMatrixWorld();
    camera.updateProjectionMatrix();
    
    const updatePositions = () => {
      const now = Date.now();
      if (now - lastUpdateRef.current < 50) { // 20fps for position updates
        animationRef.current = requestAnimationFrame(updatePositions);
        return;
      }
      lastUpdateRef.current = now;
      
      const canvas = document.querySelector('canvas');
      if (!canvas || !constellations) {
        animationRef.current = requestAnimationFrame(updatePositions);
        return;
      }
      
      // Try to get actual camera from R3F
      let actualCamera = globalCameraRef;
      if (!actualCamera) {
        try {
          const r3f = canvas.__r3f;
          if (r3f) {
            actualCamera = r3f.fiber?.root?.getState?.()?.camera 
              || r3f.root?.getState?.()?.camera
              || r3f.state?.camera;
          }
        } catch (e) { /* ignore */ }
      }
      
      // Use actual camera if available, otherwise use default
      const projCamera = actualCamera || camera;
      
      const rect = canvas.getBoundingClientRect();
      const newPositions = [];
      
      constellations.forEach((constellation) => {
        const pos = sphericalToCartesian(constellation.theta, constellation.phi, 25);
        const labelPos = new THREE.Vector3(pos.x, pos.y + 7, pos.z);
        
        // Clone position and project
        const projected = labelPos.clone();
        projected.project(projCamera);
        
        const x = (projected.x * 0.5 + 0.5) * rect.width;
        const y = (-projected.y * 0.5 + 0.5) * rect.height;
        const isVisible = projected.z < 1 && projected.z > -1;
        const depth = 1 - projected.z;
        
        newPositions.push({
          id: constellation.id,
          x,
          y,
          isVisible,
          depth,
          constellation
        });
      });
      
      setLabelPositions(newPositions);
      animationRef.current = requestAnimationFrame(updatePositions);
    };
    
    // Start after Canvas is ready
    const timeoutId = setTimeout(() => {
      animationRef.current = requestAnimationFrame(updatePositions);
    }, 1500);
    
    return () => {
      clearTimeout(timeoutId);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [constellations]);
  
  if (labelPositions.length === 0) {
    return null;
  }
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 20 }} data-testid="constellation-labels">
      {labelPositions.map((pos) => (
        pos.isVisible && (
          <div
            key={pos.id}
            className="absolute pointer-events-auto cursor-pointer hover:scale-110"
            style={{
              left: pos.x,
              top: pos.y,
              transform: 'translate(-50%, -50%)',
              zIndex: Math.round(pos.depth * 100) + 20,
              transition: 'transform 0.1s ease-out',
            }}
            onClick={() => onSelectConstellation(pos.constellation)}
          >
            <div 
              className="px-3 py-1.5 rounded-full text-xs font-semibold text-white whitespace-nowrap"
              style={{ 
                backgroundColor: `${pos.constellation.color}DD`,
                boxShadow: `0 0 15px ${pos.constellation.color}, 0 2px 8px rgba(0,0,0,0.3)`,
                textShadow: '0 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              {pos.constellation.name}
            </div>
          </div>
        )
      ))}
    </div>
  );
};

// Component inside Canvas that stores camera reference
const CameraRefCapture = () => {
  const { camera } = useThree();
  
  useLayoutEffect(() => {
    globalCameraRef = camera;
    return () => { globalCameraRef = null; };
  }, [camera]);
  
  return null;
};

// All constellations managed outside React render cycle
const AllConstellations3D = ({ constellations, constellationProgress, onSelectConstellation, newStarRealm, onIgnitionComplete, childId, selectedConstellationId, onCameraZoom }) => {
  const { scene, camera } = useThree();
  const ignitionRef = useRef(null);
  const ignitionTimeoutRef = useRef(null);
  
  // Track pointer state for click vs drag detection
  const pointerState = useRef({
    isDown: false,
    startX: 0,
    startY: 0,
    startTime: 0
  });
  
  // Create a hash of progress AND childId to detect changes
  const progressHash = JSON.stringify({ progress: constellationProgress || {}, childId: childId || '' });
  const lastProgressHashRef = useRef('');
  
  // Create or reuse constellation group
  useLayoutEffect(() => {
    // Check if progress has changed - if so, rebuild the constellation visuals
    const needsRebuild = progressHash !== lastProgressHashRef.current;
    
    if (needsRebuild && constellationGroupCache && constellationSceneRef) {
      // Remove old group from scene
      constellationSceneRef.remove(constellationGroupCache);
      constellationGroupCache = null;
      constellationSceneRef = null;
    }
    lastProgressHashRef.current = progressHash;
    
    // If we have a cached group and it's not in the current scene, add it
    if (constellationGroupCache && constellationSceneRef !== scene) {
      scene.add(constellationGroupCache);
      constellationSceneRef = scene;
      return;
    }
    
    // Only create once (unless progress changed)
    if (constellationGroupCache) {
      if (!scene.children.includes(constellationGroupCache)) {
        scene.add(constellationGroupCache);
      }
      return;
    }
    
    // Create new group for constellations
    const group = new THREE.Group();
    group.name = 'constellations-group';
    
    // Planet visual configurations for each journey
    const planetConfigs = {
      'ancient_worlds': {
        hasRings: true,
        ringColor: '#F59E0B',
        atmosphereColor: '#FCD34D',
        surfaceVariation: 0.3,
        glowIntensity: 0.4
      },
      'creativity_music': {
        hasRings: false,
        atmosphereColor: '#F472B6',
        surfaceVariation: 0.2,
        glowIntensity: 0.5,
        hasMoons: true
      },
      'coding_logic': {
        hasRings: true,
        ringColor: '#34D399',
        atmosphereColor: '#6EE7B7',
        surfaceVariation: 0.15,
        glowIntensity: 0.35
      },
      'strategy_chess': {
        hasRings: false,
        atmosphereColor: '#A78BFA',
        surfaceVariation: 0.25,
        glowIntensity: 0.45,
        hasCrystalRing: true
      },
      'nature_exploration': {
        hasRings: false,
        atmosphereColor: '#67E8F9',
        surfaceVariation: 0.2,
        glowIntensity: 0.4,
        hasAuroraEffect: true
      }
    };
    
    // Create all constellation/planet meshes
    constellations.forEach((constellation) => {
      const pos = sphericalToCartesian(constellation.theta, constellation.phi, 25);
      const config = planetConfigs[constellation.id] || {};
      
      // Create container group for this planet
      const planetGroup = new THREE.Group();
      planetGroup.position.set(pos.x, pos.y, pos.z);
      planetGroup.userData = { constellation };
      
      // === PLANET CORE ===
      const planetRadius = 3;
      const planetGeometry = new THREE.SphereGeometry(planetRadius, 48, 48);
      const planetMaterial = new THREE.MeshBasicMaterial({ 
        color: constellation.color
      });
      const planet = new THREE.Mesh(planetGeometry, planetMaterial);
      planet.userData = { constellation, isClickable: true };
      planetGroup.add(planet);
      
      // === ATMOSPHERE GLOW (inner) ===
      const atmosphereGeometry = new THREE.SphereGeometry(planetRadius * 1.15, 32, 32);
      const atmosphereMaterial = new THREE.MeshBasicMaterial({ 
        color: config.atmosphereColor || constellation.color, 
        transparent: true, 
        opacity: 0.25,
        side: THREE.BackSide
      });
      const atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);
      planetGroup.add(atmosphere);
      
      // === OUTER GLOW (ethereal aura) ===
      const outerGlowGeometry = new THREE.SphereGeometry(planetRadius * 1.8, 32, 32);
      const outerGlowMaterial = new THREE.MeshBasicMaterial({ 
        color: constellation.color, 
        transparent: true, 
        opacity: config.glowIntensity || 0.15
      });
      const outerGlow = new THREE.Mesh(outerGlowGeometry, outerGlowMaterial);
      planetGroup.add(outerGlow);
      
      // === RINGS (for planets that have them) ===
      if (config.hasRings) {
        const ringGeometry = new THREE.RingGeometry(planetRadius * 1.5, planetRadius * 2.2, 64);
        const ringMaterial = new THREE.MeshBasicMaterial({ 
          color: config.ringColor || constellation.color, 
          transparent: true, 
          opacity: 0.4,
          side: THREE.DoubleSide
        });
        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.rotation.x = Math.PI / 2.5; // Tilt the rings
        ring.rotation.y = Math.random() * 0.3; // Slight random rotation
        planetGroup.add(ring);
      }
      
      // === CRYSTAL RING (for Strategy & Chess) ===
      if (config.hasCrystalRing) {
        const crystalCount = 8;
        for (let i = 0; i < crystalCount; i++) {
          const angle = (i / crystalCount) * Math.PI * 2;
          const crystalGeometry = new THREE.OctahedronGeometry(0.3, 0);
          const crystalMaterial = new THREE.MeshBasicMaterial({ 
            color: '#E0E7FF', 
            transparent: true, 
            opacity: 0.7
          });
          const crystal = new THREE.Mesh(crystalGeometry, crystalMaterial);
          crystal.position.set(
            Math.cos(angle) * planetRadius * 1.8,
            0,
            Math.sin(angle) * planetRadius * 1.8
          );
          crystal.rotation.y = angle;
          planetGroup.add(crystal);
        }
      }
      
      // === SMALL MOONS (for Creativity & Music) ===
      if (config.hasMoons) {
        const moonGeometry = new THREE.SphereGeometry(0.4, 16, 16);
        const moonMaterial = new THREE.MeshBasicMaterial({ color: '#FDF2F8' });
        
        const moon1 = new THREE.Mesh(moonGeometry, moonMaterial);
        moon1.position.set(planetRadius * 2, planetRadius * 0.5, 0);
        planetGroup.add(moon1);
        
        const moon2 = new THREE.Mesh(moonGeometry.clone(), moonMaterial.clone());
        moon2.position.set(-planetRadius * 1.5, -planetRadius * 0.8, planetRadius);
        moon2.scale.setScalar(0.6);
        planetGroup.add(moon2);
      }
      
      // === CONSTELLATION ZONE HINT (orbital path where stars will form) ===
      const constellationZoneGeometry = new THREE.TorusGeometry(planetRadius * 4, 0.08, 8, 64);
      const constellationZoneMaterial = new THREE.MeshBasicMaterial({ 
        color: constellation.color, 
        transparent: true, 
        opacity: 0.15  // Fainter orbital ring
      });
      const constellationZone = new THREE.Mesh(constellationZoneGeometry, constellationZoneMaterial);
      constellationZone.rotation.x = Math.PI / 2.5; // Slight tilt for 3D effect
      constellationZone.rotation.z = Math.random() * 0.5; // Subtle random rotation
      planetGroup.add(constellationZone);
      
      // === DYNAMIC CONSTELLATION STARS BASED ON PROGRESS ===
      // Get the constellation pattern for this journey
      const pattern = JOURNEY_CONSTELLATION_PATTERNS[constellation.id];
      const realmId = JOURNEY_TO_REALM[constellation.id];
      
      // Get progress for this journey (0 to 1)
      const progress = constellationProgress?.[constellation.id] || 0;
      const totalStars = pattern?.totalStars || 8;
      const litStarsCount = Math.floor(progress * totalStars);
      const isComplete = litStarsCount >= totalStars;
      
      // Store star positions for line drawing
      const starPositions = [];
      
      if (pattern?.stars) {
        pattern.stars.forEach((starDef, i) => {
          const isLit = i < litStarsCount;
          
          // Calculate star position
          const x = Math.cos(starDef.angle) * planetRadius * starDef.distance;
          const y = starDef.height;
          const z = Math.sin(starDef.angle) * planetRadius * starDef.distance;
          
          starPositions.push(new THREE.Vector3(x, y, z));
          
          if (isLit) {
            // Check if this is the newly ignited star (latest star in the constellation)
            const isNewStar = newStarRealm === constellation.id && i === litStarsCount - 1;
            
            // === LIT STAR (completed lesson) ===
            // Bright core
            const starCoreGeometry = new THREE.SphereGeometry(0.35, 16, 16);
            const starCoreMaterial = new THREE.MeshBasicMaterial({ 
              color: '#FFFFFF'
            });
            const starCore = new THREE.Mesh(starCoreGeometry, starCoreMaterial);
            starCore.position.set(x, y, z);
            planetGroup.add(starCore);
            
            // Inner glow - journey color
            const innerGlowGeometry = new THREE.SphereGeometry(0.6, 12, 12);
            const innerGlowMaterial = new THREE.MeshBasicMaterial({ 
              color: constellation.color, 
              transparent: true, 
              opacity: 0.7
            });
            const innerGlow = new THREE.Mesh(innerGlowGeometry, innerGlowMaterial);
            innerGlow.position.set(x, y, z);
            planetGroup.add(innerGlow);
            
            // Outer glow - ethereal halo
            const outerGlowGeometry = new THREE.SphereGeometry(1.0, 8, 8);
            const outerGlowMaterial = new THREE.MeshBasicMaterial({ 
              color: '#FFFFFF', 
              transparent: true, 
              opacity: 0.2
            });
            const outerGlow = new THREE.Mesh(outerGlowGeometry, outerGlowMaterial);
            outerGlow.position.set(x, y, z);
            planetGroup.add(outerGlow);
            
            // === NEW STAR IGNITION EFFECT ===
            if (isNewStar) {
              // Create ignition burst group
              const ignitionGroup = new THREE.Group();
              ignitionGroup.name = 'star-ignition';
              ignitionGroup.position.set(x, y, z);
              
              // Bright flash core (will animate)
              const flashGeometry = new THREE.SphereGeometry(2.5, 16, 16);
              const flashMaterial = new THREE.MeshBasicMaterial({ 
                color: '#FFFFFF', 
                transparent: true, 
                opacity: 0.9
              });
              const flash = new THREE.Mesh(flashGeometry, flashMaterial);
              flash.userData = { type: 'flash', startScale: 0.1, targetScale: 1.0 };
              flash.scale.setScalar(0.1);
              ignitionGroup.add(flash);
              
              // Inner burst ring
              const burstGeometry = new THREE.TorusGeometry(1.5, 0.3, 8, 32);
              const burstMaterial = new THREE.MeshBasicMaterial({ 
                color: constellation.color, 
                transparent: true, 
                opacity: 0.8
              });
              const burst = new THREE.Mesh(burstGeometry, burstMaterial);
              burst.userData = { type: 'burst', startScale: 0.1, targetScale: 3.0 };
              burst.scale.setScalar(0.1);
              ignitionGroup.add(burst);
              
              // Outer golden ring
              const ringGeometry = new THREE.TorusGeometry(2.0, 0.15, 8, 32);
              const ringMaterial = new THREE.MeshBasicMaterial({ 
                color: '#FFD700', 
                transparent: true, 
                opacity: 0.6
              });
              const ring = new THREE.Mesh(ringGeometry, ringMaterial);
              ring.userData = { type: 'ring', startScale: 0.1, targetScale: 4.0 };
              ring.scale.setScalar(0.1);
              ignitionGroup.add(ring);
              
              // Sparkle particles (rays)
              for (let p = 0; p < 8; p++) {
                const rayAngle = (p / 8) * Math.PI * 2;
                const rayGeometry = new THREE.CylinderGeometry(0.05, 0.15, 2, 4);
                const rayMaterial = new THREE.MeshBasicMaterial({ 
                  color: '#FFF8DC', 
                  transparent: true, 
                  opacity: 0.8
                });
                const ray = new THREE.Mesh(rayGeometry, rayMaterial);
                ray.rotation.z = rayAngle + Math.PI / 2;
                ray.userData = { 
                  type: 'ray', 
                  angle: rayAngle, 
                  startDist: 0.5, 
                  targetDist: 3.0
                };
                ray.position.set(
                  Math.cos(rayAngle) * 0.5,
                  Math.sin(rayAngle) * 0.5,
                  0
                );
                ignitionGroup.add(ray);
              }
              
              planetGroup.add(ignitionGroup);
              
              // Store reference to animate
              if (!ignitionRef.current) {
                ignitionRef.current = { group: ignitionGroup, startTime: Date.now(), duration: 1500 };
              }
            }
            
            // Add sparkle rays for extra visual impact
            if (isComplete) {
              // Extra shimmer for completed constellations
              const shimmerGeometry = new THREE.SphereGeometry(1.3, 8, 8);
              const shimmerMaterial = new THREE.MeshBasicMaterial({ 
                color: constellation.color, 
                transparent: true, 
                opacity: 0.15
              });
              const shimmer = new THREE.Mesh(shimmerGeometry, shimmerMaterial);
              shimmer.position.set(x, y, z);
              planetGroup.add(shimmer);
            }
          } else {
            // === DIM STAR (unearned - waiting for lesson completion) ===
            // Faint core - hint of where star will appear
            const dimStarGeometry = new THREE.SphereGeometry(0.2, 8, 8);
            const dimStarMaterial = new THREE.MeshBasicMaterial({ 
              color: '#FFFFFF', 
              transparent: true, 
              opacity: 0.15
            });
            const dimStar = new THREE.Mesh(dimStarGeometry, dimStarMaterial);
            dimStar.position.set(x, y, z);
            planetGroup.add(dimStar);
            
            // Very faint glow
            const dimGlowGeometry = new THREE.SphereGeometry(0.4, 8, 8);
            const dimGlowMaterial = new THREE.MeshBasicMaterial({ 
              color: constellation.color, 
              transparent: true, 
              opacity: 0.05
            });
            const dimGlow = new THREE.Mesh(dimGlowGeometry, dimGlowMaterial);
            dimGlow.position.set(x, y, z);
            planetGroup.add(dimGlow);
          }
        });
        
        // === CONSTELLATION LINES ===
        // Draw lines between stars based on pattern connections
        if (pattern.connections) {
          pattern.connections.forEach(([fromIdx, toIdx]) => {
            // Only draw line if both stars are lit
            const bothLit = fromIdx < litStarsCount && toIdx < litStarsCount;
            const fromPos = starPositions[fromIdx];
            const toPos = starPositions[toIdx];
            
            if (fromPos && toPos) {
              const lineGeometry = new THREE.BufferGeometry().setFromPoints([fromPos, toPos]);
              const lineMaterial = new THREE.LineBasicMaterial({ 
                color: bothLit ? constellation.color : '#FFFFFF', 
                transparent: true, 
                opacity: bothLit ? 0.6 : 0.03  // Bright if connected, very faint preview otherwise
              });
              const line = new THREE.Line(lineGeometry, lineMaterial);
              planetGroup.add(line);
            }
          });
        }
      } else {
        // Fallback: Create simple ring of placeholder stars if no pattern defined
        const starPointCount = 7;
        for (let i = 0; i < starPointCount; i++) {
          const angle = (i / starPointCount) * Math.PI * 2;
          const orbitRadius = planetRadius * 4;
          const height = (Math.sin(angle * 2) * 1.5);
          
          const starCoreGeometry = new THREE.SphereGeometry(0.2, 8, 8);
          const starCoreMaterial = new THREE.MeshBasicMaterial({ 
            color: '#FFFFFF', 
            transparent: true, 
            opacity: 0.3
          });
          const starCore = new THREE.Mesh(starCoreGeometry, starCoreMaterial);
          starCore.position.set(
            Math.cos(angle) * orbitRadius,
            height,
            Math.sin(angle) * orbitRadius
          );
          planetGroup.add(starCore);
        }
      }
      
      group.add(planetGroup);
    });
    
    // Cache the group
    constellationGroupCache = group;
    constellationSceneRef = scene;
    
    // Add group to scene
    scene.add(group);
    
    // NO CLEANUP - the cache logic at the start of useEffect handles clearing
    // when progressHash changes. Cleanup here would cause planets to disappear.
  }, [scene, constellations, progressHash, newStarRealm, childId]);
  
  // Camera zoom to selected constellation
  useEffect(() => {
    if (!selectedConstellationId || !camera) return;
    
    // Find the selected constellation's position
    const constellation = JOURNEY_CONSTELLATIONS_3D.find(c => c.id === selectedConstellationId);
    if (!constellation) return;
    
    const targetPos = sphericalToCartesian(constellation.theta, constellation.phi, 25);
    
    // Calculate camera position that looks at the constellation from a closer distance
    const cameraDistance = 35; // Move closer than default 50
    const cameraTarget = new THREE.Vector3(targetPos.x, targetPos.y, targetPos.z);
    const direction = cameraTarget.clone().normalize();
    const newCameraPos = direction.multiplyScalar(cameraDistance);
    
    // Animate camera to position
    const startPos = camera.position.clone();
    const startTime = Date.now();
    const duration = 800; // 0.8 second zoom
    
    const animateCamera = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      
      camera.position.lerpVectors(startPos, newCameraPos, easeProgress);
      camera.lookAt(cameraTarget);
      
      if (progress < 1) {
        requestAnimationFrame(animateCamera);
      } else if (onCameraZoom) {
        onCameraZoom(true); // Notify zoom complete
      }
    };
    
    animateCamera();
  }, [selectedConstellationId, camera, onCameraZoom]);
  
  // Animate the star ignition effect
  useEffect(() => {
    if (!ignitionRef.current) return;
    
    const animate = () => {
      if (!ignitionRef.current) return;
      
      const { group, startTime, duration } = ignitionRef.current;
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const easeOutBack = 1 + 2.70158 * Math.pow(progress - 1, 3) + 1.70158 * Math.pow(progress - 1, 2);
      
      // Animate each child element
      group.children.forEach(child => {
        const userData = child.userData;
        
        if (userData.type === 'flash') {
          // Flash expands and fades
          const scale = userData.startScale + (userData.targetScale - userData.startScale) * easeOutQuart;
          child.scale.setScalar(scale);
          child.material.opacity = 0.9 * (1 - progress);
        } else if (userData.type === 'burst' || userData.type === 'ring') {
          // Rings expand outward and fade
          const scale = userData.startScale + (userData.targetScale - userData.startScale) * easeOutBack;
          child.scale.setScalar(scale);
          child.material.opacity = (userData.type === 'burst' ? 0.8 : 0.6) * (1 - progress * 0.8);
          child.rotation.z += 0.02; // Gentle spin
        } else if (userData.type === 'ray') {
          // Rays shoot outward and fade
          const dist = userData.startDist + (userData.targetDist - userData.startDist) * easeOutQuart;
          child.position.set(
            Math.cos(userData.angle) * dist,
            Math.sin(userData.angle) * dist,
            0
          );
          child.material.opacity = 0.8 * (1 - progress);
        }
      });
      
      if (progress < 1) {
        ignitionTimeoutRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete - remove ignition group and notify
        if (group.parent) {
          group.parent.remove(group);
        }
        ignitionRef.current = null;
        if (onIgnitionComplete) {
          onIgnitionComplete();
        }
      }
    };
    
    animate();
    
    return () => {
      if (ignitionTimeoutRef.current) {
        cancelAnimationFrame(ignitionTimeoutRef.current);
      }
    };
  }, [newStarRealm, onIgnitionComplete]);
  
  // Handle click events via raycasting - differentiate click from drag
  useEffect(() => {
    if (!constellationGroupCache) return;
    
    const canvas = document.querySelector('canvas');
    if (!canvas) return;
    
    const handlePointerDown = (event) => {
      pointerState.current = {
        isDown: true,
        startX: event.clientX,
        startY: event.clientY,
        startTime: Date.now()
      };
    };
    
    const handlePointerUp = (event) => {
      if (!pointerState.current.isDown) return;
      
      const dx = event.clientX - pointerState.current.startX;
      const dy = event.clientY - pointerState.current.startY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const duration = Date.now() - pointerState.current.startTime;
      
      pointerState.current.isDown = false;
      
      // Only treat as click if pointer moved less than 5px and duration less than 300ms
      if (distance < 5 && duration < 300) {
        // This is a click, not a drag
        const rect = canvas.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        
        const raycaster = new THREE.Raycaster();
        const mouseVec = new THREE.Vector2(x, y);
        raycaster.setFromCamera(mouseVec, camera);
        
        // Get all clickable meshes
        const clickableMeshes = [];
        constellationGroupCache.traverse((obj) => {
          if (obj.userData?.isClickable) {
            clickableMeshes.push(obj);
          }
        });
        
        const intersects = raycaster.intersectObjects(clickableMeshes);
        
        if (intersects.length > 0) {
          const clickedMesh = intersects[0].object;
          const constellation = clickedMesh.userData?.constellation;
          if (constellation && onSelectConstellation) {
            onSelectConstellation(constellation);
          }
        }
      }
    };
    
    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointerup', handlePointerUp);
    
    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointerup', handlePointerUp);
    };
  }, [camera, onSelectConstellation]);
  
  return null;
};

// ============ CUSTOM CONSTELLATIONS 3D COMPONENT ============
// Renders child's saved custom constellations in a "My Creations" zone
const CustomConstellations3D = ({ customConstellations, onSelectCustomConstellation, expandedConstellationId }) => {
  const { scene, invalidate } = useThree();
  const groupRef = useRef(null);
  const animationRef = useRef(null);
  const [expansionProgress, setExpansionProgress] = useState({});
  
  // Animation loop for smooth expand/collapse transitions
  useEffect(() => {
    if (!customConstellations || customConstellations.length === 0) return;
    
    let animating = true;
    const progressRef = { ...expansionProgress };
    
    const animate = () => {
      if (!animating) return;
      
      let needsUpdate = false;
      
      customConstellations.forEach((constellation) => {
        const isExpanded = expandedConstellationId === constellation.id;
        const currentProgress = progressRef[constellation.id] || 0;
        const targetProgress = isExpanded ? 1 : 0;
        
        // Smooth lerp towards target (easing)
        const diff = targetProgress - currentProgress;
        if (Math.abs(diff) > 0.01) {
          progressRef[constellation.id] = currentProgress + diff * 0.12; // Smooth easing
          needsUpdate = true;
        } else if (Math.abs(currentProgress - targetProgress) > 0.001) {
          progressRef[constellation.id] = targetProgress;
          needsUpdate = true;
        }
      });
      
      // Update state to trigger re-render
      if (needsUpdate) {
        setExpansionProgress({ ...progressRef });
        invalidate(); // Tell R3F to re-render
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      animating = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [customConstellations, expandedConstellationId, invalidate]);
  
  useLayoutEffect(() => {
    if (!customConstellations || customConstellations.length === 0) return;
    
    // Remove old group if exists
    if (groupRef.current && scene.children.includes(groupRef.current)) {
      scene.remove(groupRef.current);
    }
    
    // Create new group for custom constellations
    const group = new THREE.Group();
    group.name = 'custom-constellations-group';
    
    customConstellations.forEach((constellation, index) => {
      const isExpanded = expandedConstellationId === constellation.id;
      const progress = expansionProgress[constellation.id] || (isExpanded ? 1 : 0);
      
      // Generate a unique color based on constellation name
      const hash = constellation.name.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0);
      const hue = Math.abs(hash) % 360;
      const constellationColor = `hsl(${hue}, 85%, 70%)`;
      const constellationColorHex = new THREE.Color(constellationColor);
      
      // Create container group for this custom constellation
      const constellationGroup = new THREE.Group();
      
      // COLLAPSED STATE: Position as a marker in the upper sky
      // EXPANDED STATE: Move to CENTER of view for dramatic reveal
      const collapsedTheta = Math.PI * (0.55 + index * 0.15);
      const collapsedPhi = Math.PI / 2.3;
      const collapsedRadius = 28;
      
      // When expanded, position at center-front of view
      const expandedTheta = Math.PI * 0.1; // Almost directly in front
      const expandedPhi = Math.PI / 2.5;
      const expandedRadius = 20; // Closer to camera
      
      // Interpolate position based on expansion progress
      const theta = collapsedTheta + (expandedTheta - collapsedTheta) * progress;
      const phi = collapsedPhi + (expandedPhi - collapsedPhi) * progress;
      const radius = collapsedRadius + (expandedRadius - collapsedRadius) * progress;
      
      const centerPos = sphericalToCartesian(theta, phi, radius);
      constellationGroup.position.set(centerPos.x, centerPos.y, centerPos.z);
      constellationGroup.userData = { customConstellation: constellation, constellationId: constellation.id };
      
      // Store 3D star positions for line connections
      const star3DPositions = [];
      
      // Scale factor - DRAMATICALLY LARGER when expanded
      // Collapsed: small marker, Expanded: fills a significant portion of sky
      const baseScale = 0.05; // Small when collapsed
      const expandedScale = 0.35; // Much larger when expanded
      const currentScale = baseScale + (expandedScale - baseScale) * progress;
      
      // Calculate center of stars
      let starCenterX = 0, starCenterY = 0;
      constellation.stars.forEach((star) => {
        starCenterX += (star.x - 50) * currentScale;
        starCenterY += (50 - star.y) * currentScale;
      });
      starCenterX /= constellation.stars.length || 1;
      starCenterY /= constellation.stars.length || 1;
      
      // === RENDER STARS ===
      constellation.stars.forEach((star, starIdx) => {
        // Target positions spread out based on current scale
        const targetX = (star.x - 50) * currentScale;
        const targetY = (50 - star.y) * currentScale;
        const targetZ = (Math.sin(starIdx * 0.7) * 2) * progress; // More depth when expanded
        
        // When collapsed, all stars cluster at center; when expanded, spread to positions
        const localX = starCenterX + (targetX - starCenterX) * progress;
        const localY = starCenterY + (targetY - starCenterY) * progress;
        const localZ = targetZ;
        
        star3DPositions.push(new THREE.Vector3(localX, localY, localZ));
        
        // Star size: tiny when collapsed, LARGE when expanded (like small planets)
        const starBaseSize = 0.3; // Small collapsed
        const starExpandedSize = 1.8; // Large when expanded
        const starSize = starBaseSize + (starExpandedSize - starBaseSize) * progress;
        const opacity = 0.4 + (0.6 * progress);
        
        // === STAR CORE (bright white center) ===
        const starCoreGeometry = new THREE.SphereGeometry(starSize * 0.5, 16, 16);
        const starCoreMaterial = new THREE.MeshBasicMaterial({ 
          color: '#FFFFFF',
          transparent: true,
          opacity: opacity
        });
        const starCore = new THREE.Mesh(starCoreGeometry, starCoreMaterial);
        starCore.position.set(localX, localY, localZ);
        constellationGroup.add(starCore);
        
        // === STAR GLOW (colored halo) ===
        const glowGeometry = new THREE.SphereGeometry(starSize * 0.8, 12, 12);
        const glowMaterial = new THREE.MeshBasicMaterial({ 
          color: constellationColorHex, 
          transparent: true, 
          opacity: 0.6 * opacity
        });
        const glow = new THREE.Mesh(glowGeometry, glowMaterial);
        glow.position.set(localX, localY, localZ);
        constellationGroup.add(glow);
        
        // === OUTER SPARKLE (large glow halo - only when expanding) ===
        if (progress > 0.2) {
          const sparkleSize = starSize * 1.5;
          const sparkleGeometry = new THREE.SphereGeometry(sparkleSize, 8, 8);
          const sparkleMaterial = new THREE.MeshBasicMaterial({ 
            color: constellationColorHex, 
            transparent: true, 
            opacity: 0.25 * progress
          });
          const sparkle = new THREE.Mesh(sparkleGeometry, sparkleMaterial);
          sparkle.position.set(localX, localY, localZ);
          constellationGroup.add(sparkle);
        }
        
        // === STARBURST RAYS (cross flare effect when fully expanded) ===
        if (progress > 0.6) {
          const rayOpacity = (progress - 0.6) / 0.4 * 0.5;
          const rayMaterial = new THREE.MeshBasicMaterial({
            color: '#FFFFFF',
            transparent: true,
            opacity: rayOpacity,
            side: THREE.DoubleSide
          });
          
          // Horizontal ray
          const hRayGeometry = new THREE.PlaneGeometry(starSize * 4, starSize * 0.2);
          const hRay = new THREE.Mesh(hRayGeometry, rayMaterial);
          hRay.position.set(localX, localY, localZ + 0.01);
          constellationGroup.add(hRay);
          
          // Vertical ray
          const vRayGeometry = new THREE.PlaneGeometry(starSize * 0.2, starSize * 4);
          const vRay = new THREE.Mesh(vRayGeometry, rayMaterial);
          vRay.position.set(localX, localY, localZ + 0.01);
          constellationGroup.add(vRay);
        }
      });
      
      // === CONSTELLATION LINES (thick glowing lines connecting stars) ===
      if (progress > 0.15 && constellation.connections) {
        const lineOpacity = Math.min(1, (progress - 0.15) / 0.35);
        constellation.connections.forEach(([fromIdx, toIdx]) => {
          const fromPos = star3DPositions[fromIdx];
          const toPos = star3DPositions[toIdx];
          
          if (fromPos && toPos) {
            // Create thick tube for highly visible lines
            const direction = new THREE.Vector3().subVectors(toPos, fromPos);
            const distance = direction.length();
            
            // Main connection line (tube)
            const tubeRadius = 0.15 + (0.25 * progress); // Thicker when expanded
            const tubeGeometry = new THREE.CylinderGeometry(tubeRadius, tubeRadius, distance, 8);
            const tubeMaterial = new THREE.MeshBasicMaterial({ 
              color: constellationColorHex, 
              transparent: true, 
              opacity: 0.9 * lineOpacity
            });
            const tube = new THREE.Mesh(tubeGeometry, tubeMaterial);
            
            // Position at midpoint and rotate to align
            const midpoint = new THREE.Vector3().addVectors(fromPos, toPos).multiplyScalar(0.5);
            tube.position.copy(midpoint);
            tube.lookAt(toPos);
            tube.rotateX(Math.PI / 2);
            constellationGroup.add(tube);
            
            // Outer glow tube for extra visibility
            const glowTubeRadius = tubeRadius * 2.5;
            const glowTubeGeometry = new THREE.CylinderGeometry(glowTubeRadius, glowTubeRadius, distance, 8);
            const glowTubeMaterial = new THREE.MeshBasicMaterial({ 
              color: constellationColorHex, 
              transparent: true, 
              opacity: 0.2 * lineOpacity
            });
            const glowTube = new THREE.Mesh(glowTubeGeometry, glowTubeMaterial);
            glowTube.position.copy(midpoint);
            glowTube.lookAt(toPos);
            glowTube.rotateX(Math.PI / 2);
            constellationGroup.add(glowTube);
          }
        });
      }
      
      // === CENTRAL MARKER (glowing beacon - larger when collapsed) ===
      const markerPulse = Math.sin(Date.now() * 0.004) * 0.2;
      const markerSize = isExpanded ? (0.8 * (1 - progress * 0.7)) : (1.2 + markerPulse);
      const markerOpacity = isExpanded ? (0.4 * (1 - progress)) : 0.95;
      
      if (markerOpacity > 0.05) {
        // Main marker sphere
        const markerGeometry = new THREE.SphereGeometry(markerSize, 16, 16);
        const markerMaterial = new THREE.MeshBasicMaterial({ 
          color: constellationColorHex, 
          transparent: true, 
          opacity: markerOpacity
        });
        const markerSphere = new THREE.Mesh(markerGeometry, markerMaterial);
        markerSphere.position.set(starCenterX, starCenterY, 0);
        markerSphere.userData = { customConstellation: constellation, isClickable: true, isMarker: true };
        constellationGroup.add(markerSphere);
        
        // Outer glow for marker
        const markerGlowGeometry = new THREE.SphereGeometry(markerSize * 2, 12, 12);
        const markerGlowMaterial = new THREE.MeshBasicMaterial({ 
          color: constellationColorHex, 
          transparent: true, 
          opacity: markerOpacity * 0.3
        });
        const markerGlow = new THREE.Mesh(markerGlowGeometry, markerGlowMaterial);
        markerGlow.position.set(starCenterX, starCenterY, 0);
        constellationGroup.add(markerGlow);
      }
      
      // === PULSING RING (visible when collapsed to draw attention) ===
      if (!isExpanded || progress < 0.5) {
        const ringPulse = Math.sin(Date.now() * 0.005) * 0.2;
        const ringOpacity = (0.6 + ringPulse) * (1 - progress);
        const ringSize = 2 + ringPulse;
        
        if (ringOpacity > 0.05) {
          const ringGeometry = new THREE.TorusGeometry(ringSize, 0.15, 8, 32);
          const ringMaterial = new THREE.MeshBasicMaterial({ 
            color: constellationColorHex, 
            transparent: true, 
            opacity: ringOpacity
          });
          const ring = new THREE.Mesh(ringGeometry, ringMaterial);
          ring.position.set(starCenterX, starCenterY, 0);
          ring.rotation.x = Math.PI / 2;
          constellationGroup.add(ring);
        }
      }
      
      // === EXPANDED CONSTELLATION BOUNDARY (decorative ring when expanded) ===
      if (progress > 0.4) {
        const boundaryOpacity = (progress - 0.4) * 0.5;
        // Calculate constellation span for ring size
        let maxSpan = 0;
        constellation.stars.forEach(star => {
          const dx = Math.abs(star.x - 50) * currentScale;
          const dy = Math.abs(50 - star.y) * currentScale;
          maxSpan = Math.max(maxSpan, Math.sqrt(dx*dx + dy*dy));
        });
        const boundarySize = maxSpan * 1.5 + 2;
        
        const boundaryGeometry = new THREE.TorusGeometry(boundarySize, 0.06, 8, 48);
        const boundaryMaterial = new THREE.MeshBasicMaterial({ 
          color: constellationColorHex, 
          transparent: true, 
          opacity: boundaryOpacity
        });
        const boundaryRing = new THREE.Mesh(boundaryGeometry, boundaryMaterial);
        boundaryRing.rotation.x = Math.PI / 2;
        constellationGroup.add(boundaryRing);
      }
      
      group.add(constellationGroup);
    });
    
    groupRef.current = group;
    scene.add(group);
    
    return () => {
      if (groupRef.current && scene.children.includes(groupRef.current)) {
        scene.remove(groupRef.current);
      }
    };
  }, [scene, customConstellations, expandedConstellationId, expansionProgress]);
  
  // Handle click events for custom constellations
  const { camera } = useThree();
  
  useEffect(() => {
    if (!groupRef.current) return;
    
    const canvas = document.querySelector('canvas');
    if (!canvas) return;
    
    const pointerState = { isDown: false, startX: 0, startY: 0, startTime: 0 };
    
    const handlePointerDown = (event) => {
      pointerState.isDown = true;
      pointerState.startX = event.clientX;
      pointerState.startY = event.clientY;
      pointerState.startTime = Date.now();
    };
    
    const handlePointerUp = (event) => {
      if (!pointerState.isDown) return;
      
      const dx = event.clientX - pointerState.startX;
      const dy = event.clientY - pointerState.startY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const duration = Date.now() - pointerState.startTime;
      
      pointerState.isDown = false;
      
      // Only treat as click if pointer moved less than 5px and duration less than 300ms
      if (distance < 5 && duration < 300) {
        const rect = canvas.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        
        const raycaster = new THREE.Raycaster();
        const mouseVec = new THREE.Vector2(x, y);
        raycaster.setFromCamera(mouseVec, camera);
        
        // Get all clickable meshes
        const clickableMeshes = [];
        groupRef.current.traverse((obj) => {
          if (obj.userData?.isClickable) {
            clickableMeshes.push(obj);
          }
        });
        
        const intersects = raycaster.intersectObjects(clickableMeshes);
        
        if (intersects.length > 0) {
          const clickedMesh = intersects[0].object;
          const constellation = clickedMesh.userData?.customConstellation;
          if (constellation && onSelectCustomConstellation) {
            onSelectCustomConstellation(constellation);
          }
        }
      }
    };
    
    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointerup', handlePointerUp);
    
    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointerup', handlePointerUp);
    };
  }, [camera, onSelectCustomConstellation]);
  
  return null;
};

// DOM overlay labels for custom constellations
const CustomConstellationLabelsOverlay = ({ customConstellations, onSelectCustomConstellation, expandedConstellationId }) => {
  const [labelPositions, setLabelPositions] = useState([]);
  const animationRef = useRef(null);
  const lastUpdateRef = useRef(0);
  
  useEffect(() => {
    if (!customConstellations || customConstellations.length === 0) {
      setLabelPositions([]);
      return;
    }
    
    const baseRadius = 35;
    const basePhi = Math.PI / 4;
    
    // Create a projection matrix camera for calculations
    const camera = new THREE.PerspectiveCamera(60, 1.8, 0.1, 1000);
    camera.position.set(0, 0, 50);
    camera.lookAt(0, 0, 0);
    camera.updateMatrixWorld();
    camera.updateProjectionMatrix();
    
    const updatePositions = () => {
      const now = Date.now();
      if (now - lastUpdateRef.current < 50) {
        animationRef.current = requestAnimationFrame(updatePositions);
        return;
      }
      lastUpdateRef.current = now;
      
      const canvas = document.querySelector('canvas');
      if (!canvas) {
        animationRef.current = requestAnimationFrame(updatePositions);
        return;
      }
      
      // Try to get actual camera from R3F
      let actualCamera = globalCameraRef;
      if (!actualCamera) {
        try {
          const r3f = canvas.__r3f;
          if (r3f) {
            actualCamera = r3f.fiber?.root?.getState?.()?.camera 
              || r3f.root?.getState?.()?.camera
              || r3f.state?.camera;
          }
        } catch (e) { /* ignore */ }
      }
      
      const projCamera = actualCamera || camera;
      const rect = canvas.getBoundingClientRect();
      const newPositions = [];
      
      customConstellations.forEach((constellation, index) => {
        const thetaOffset = (index / Math.max(1, customConstellations.length)) * Math.PI * 2;
        const theta = thetaOffset;
        const phi = basePhi + (Math.sin(index * 1.5) * 0.2);
        
        const pos = sphericalToCartesian(theta, phi, baseRadius);
        const labelPos = new THREE.Vector3(pos.x, pos.y + 3, pos.z);
        
        const projected = labelPos.clone();
        projected.project(projCamera);
        
        const x = (projected.x * 0.5 + 0.5) * rect.width;
        const y = (-projected.y * 0.5 + 0.5) * rect.height;
        const isVisible = projected.z < 1 && projected.z > -1;
        const depth = 1 - projected.z;
        
        // Generate color from name hash
        const hash = constellation.name.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0);
        const hue = Math.abs(hash) % 360;
        
        newPositions.push({
          id: constellation.id,
          x,
          y,
          isVisible,
          depth,
          constellation,
          color: `hsl(${hue}, 70%, 65%)`
        });
      });
      
      setLabelPositions(newPositions);
      animationRef.current = requestAnimationFrame(updatePositions);
    };
    
    const timeoutId = setTimeout(() => {
      animationRef.current = requestAnimationFrame(updatePositions);
    }, 1500);
    
    return () => {
      clearTimeout(timeoutId);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [customConstellations]);
  
  if (labelPositions.length === 0) return null;
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 21 }} data-testid="custom-constellation-labels">
      {labelPositions.map((pos) => {
        const isExpanded = expandedConstellationId === pos.id;
        return pos.isVisible && (
          <div
            key={pos.id}
            className={`absolute pointer-events-auto cursor-pointer transition-transform duration-200 ${isExpanded ? 'scale-125' : 'hover:scale-110'}`}
            style={{
              left: pos.x,
              top: pos.y,
              transform: 'translate(-50%, -50%)',
              zIndex: Math.round(pos.depth * 100) + 21,
            }}
            onClick={() => onSelectCustomConstellation(pos.constellation)}
          >
            <div 
              className={`px-2 py-1 rounded-full text-xs font-medium text-white whitespace-nowrap flex items-center gap-1 transition-all duration-300 ${isExpanded ? 'ring-2 ring-white/50' : ''}`}
              style={{ 
                backgroundColor: isExpanded ? `${pos.color}` : `${pos.color}CC`,
                boxShadow: isExpanded 
                  ? `0 0 20px ${pos.color}, 0 0 40px ${pos.color}80, 0 2px 8px rgba(0,0,0,0.4)`
                  : `0 0 12px ${pos.color}, 0 2px 6px rgba(0,0,0,0.3)`,
                textShadow: '0 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              <Star className={`w-3 h-3 ${isExpanded ? 'animate-pulse' : ''}`} />
              {pos.constellation.name}
              {isExpanded && <span className="ml-1 text-[10px] opacity-75">✨</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Sky dome background with nebula effects
const SkyDomeBackground = () => {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.01;
    }
  });
  
  return (
    <group ref={meshRef}>
      {/* Distant nebula clouds */}
      <mesh>
        <sphereGeometry args={[100, 64, 32]} />
        <meshBasicMaterial 
          color="#0A0318" 
          side={THREE.BackSide}
        />
      </mesh>
      
      {/* Purple nebula band */}
      <mesh rotation={[Math.PI / 4, 0, 0]}>
        <torusGeometry args={[60, 15, 16, 64, Math.PI]} />
        <meshBasicMaterial color="#7C3AED" transparent opacity={0.15} side={THREE.DoubleSide} />
      </mesh>
      
      {/* Pink nebula band */}
      <mesh rotation={[Math.PI / 3, Math.PI / 4, 0]}>
        <torusGeometry args={[55, 10, 16, 64, Math.PI * 0.8]} />
        <meshBasicMaterial color="#DB2777" transparent opacity={0.12} side={THREE.DoubleSide} />
      </mesh>
      
      {/* Cyan accent */}
      <mesh rotation={[Math.PI / 2.5, -Math.PI / 3, 0]}>
        <torusGeometry args={[50, 8, 16, 64, Math.PI * 0.6]} />
        <meshBasicMaterial color="#22D3EE" transparent opacity={0.10} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
};

// Camera controller with drag to rotate - simple orbit around origin
const CameraController = ({ onCameraMove }) => {
  const controlsRef = useRef();
  
  return (
    <OrbitControls 
      ref={controlsRef}
      enableZoom={true}
      enablePan={true}
      enableRotate={true}
      rotateSpeed={0.5}
      panSpeed={0.5}
      zoomSpeed={0.8}
      minDistance={20}
      maxDistance={80}
      target={[0, 0, 0]}
      autoRotate={true}
      autoRotateSpeed={0.1}
      onChange={() => onCameraMove && onCameraMove()}
    />
  );
};

// Main 3D Sky Dome Scene
const SkyDome3D = ({ 
  constellations = JOURNEY_CONSTELLATIONS_3D,
  constellationProgress = {},
  customConstellations = [],
  onSelectConstellation,
  onSelectCustomConstellation,
  selectedConstellation,
  newStarRealm,
  onIgnitionComplete,
  childId,
  selectedConstellationId,
  expandedConstellationId
}) => {
  const [cameraZoomed, setCameraZoomed] = useState(false);
  
  // Use ref instead of state to avoid re-renders
  const isDraggingRef = useRef(false);
  const [cursorStyle, setCursorStyle] = useState('grab');
  
  // Handle drag state without causing re-render
  const handlePointerDown = useCallback(() => {
    isDraggingRef.current = true;
    setCursorStyle('grabbing');
  }, []);
  
  const handlePointerUp = useCallback(() => {
    isDraggingRef.current = false;
    setCursorStyle('grab');
  }, []);
  
  return (
    <div className="relative w-full h-[500px] sm:h-[600px] rounded-2xl overflow-hidden border border-indigo-500/30">
      {/* Drag hint overlay */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-900/90 border border-indigo-500/30 rounded-full shadow-lg">
          <Move className="w-4 h-4 text-indigo-400" />
          <span className="text-indigo-300 text-sm">Drag to explore • Click constellations to learn</span>
        </div>
      </div>
      
      {/* Constellation count indicator */}
      <div className="absolute top-4 left-4 z-20 pointer-events-none flex gap-2">
        <div className="px-3 py-1.5 bg-slate-900/90 border border-amber-500/30 rounded-full">
          <span className="text-amber-400 text-sm font-medium">5 Journey Worlds</span>
        </div>
        {customConstellations.length > 0 && (
          <div className="px-3 py-1.5 bg-slate-900/90 border border-purple-500/30 rounded-full">
            <span className="text-purple-400 text-sm font-medium">{customConstellations.length} My Creation{customConstellations.length !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
      
      {/* New Star Ignition Notification */}
      {newStarRealm && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 animate-bounce">
          <div className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500/90 to-yellow-400/90 rounded-full shadow-lg shadow-amber-500/30 border border-amber-300/50">
            <Sparkles className="w-5 h-5 text-white animate-pulse" />
            <span className="text-white font-bold">New Star Earned!</span>
            <Star className="w-5 h-5 text-white animate-spin" style={{ animationDuration: '2s' }} />
          </div>
        </div>
      )}
      
      {/* Reset view button */}
      <button 
        className="absolute top-4 right-4 z-20 p-2 bg-slate-900/80 border border-slate-700 rounded-lg hover:bg-slate-800 transition-colors"
        onClick={() => window.location.reload()}
        title="Reset view"
      >
        <RotateCcw className="w-4 h-4 text-slate-400" />
      </button>
      
      <Canvas
        camera={{ position: [0, 0, 50], fov: 60 }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        style={{ cursor: cursorStyle }}
      >
        {/* Ambient light */}
        <ambientLight intensity={0.3} />
        
        {/* Camera controls */}
        <CameraController />
        
        {/* Sky background */}
        <SkyDomeBackground />
        
        {/* Stars */}
        <Stars 
          radius={80}
          depth={20}
          count={3000}
          factor={3}
          saturation={0.5}
          fade
          speed={0.5}
        />
        
        {/* Ground reference - horizon glow */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -35, 0]}>
          <ringGeometry args={[30, 50, 64]} />
          <meshBasicMaterial color="#6366F1" transparent opacity={0.15} side={THREE.DoubleSide} />
        </mesh>
        
        {/* All Journey Constellations - render last to ensure they're on top */}
        <AllConstellations3D
          constellations={constellations}
          constellationProgress={constellationProgress}
          onSelectConstellation={onSelectConstellation}
          newStarRealm={newStarRealm}
          onIgnitionComplete={onIgnitionComplete}
          childId={childId}
          selectedConstellationId={selectedConstellationId}
          onCameraZoom={setCameraZoomed}
        />
        
        {/* Custom Child-Created Constellations */}
        {customConstellations.length > 0 && (
          <CustomConstellations3D
            customConstellations={customConstellations}
            onSelectCustomConstellation={onSelectCustomConstellation}
            expandedConstellationId={expandedConstellationId}
          />
        )}
        
        {/* Capture camera reference for label positioning */}
        <CameraRefCapture />
      </Canvas>
      
      {/* Constellation Labels - DOM overlay outside Canvas */}
      <ConstellationLabelsOverlay
        constellations={constellations}
        onSelectConstellation={onSelectConstellation}
      />
      
      {/* Custom Constellation Labels - DOM overlay */}
      {customConstellations.length > 0 && (
        <CustomConstellationLabelsOverlay
          customConstellations={customConstellations}
          onSelectCustomConstellation={onSelectCustomConstellation}
          expandedConstellationId={expandedConstellationId}
        />
      )}
      
      {/* Expanded Constellation Visualization - Clear star pattern */}
      {expandedConstellationId && customConstellations.length > 0 && (() => {
        const expandedConstellation = customConstellations.find(c => c.id === expandedConstellationId);
        if (!expandedConstellation) return null;
        
        // Generate color from name
        const hash = expandedConstellation.name.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0);
        const hue = Math.abs(hash) % 360;
        const color = `hsl(${hue}, 90%, 65%)`;
        
        return (
          <div 
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ zIndex: 25 }}
          >
            {/* Dark backdrop to make stars pop */}
            <div 
              className="absolute inset-0"
              style={{ 
                background: 'radial-gradient(circle at center, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, transparent 80%)',
              }}
            />
            
            <div 
              className="relative"
              style={{
                width: '80%',
                maxWidth: '500px',
                aspectRatio: '1',
              }}
            >
              {/* Main constellation SVG - CLEAR stars and lines */}
              <svg 
                viewBox="0 0 100 100" 
                className="w-full h-full"
              >
                {/* Thin outer ring */}
                <circle 
                  cx="50" cy="50" r="48" 
                  fill="none" 
                  stroke={color} 
                  strokeWidth="0.5" 
                  opacity="0.3"
                />
                
                {/* === CONNECTING LINES - Draw first so stars appear on top === */}
                {expandedConstellation.connections?.map(([fromIdx, toIdx], i) => {
                  const fromStar = expandedConstellation.stars?.[fromIdx];
                  const toStar = expandedConstellation.stars?.[toIdx];
                  if (!fromStar || !toStar) return null;
                  
                  return (
                    <g key={`line-${i}`}>
                      {/* Outer glow line */}
                      <line
                        x1={fromStar.x}
                        y1={fromStar.y}
                        x2={toStar.x}
                        y2={toStar.y}
                        stroke={color}
                        strokeWidth="6"
                        opacity="0.4"
                        strokeLinecap="round"
                      />
                      {/* Main bright line - WHITE for visibility */}
                      <line
                        x1={fromStar.x}
                        y1={fromStar.y}
                        x2={toStar.x}
                        y2={toStar.y}
                        stroke="#FFFFFF"
                        strokeWidth="2.5"
                        opacity="1"
                        strokeLinecap="round"
                      />
                    </g>
                  );
                })}
                
                {/* === STARS - Large, bright, clearly visible === */}
                {expandedConstellation.stars?.map((star, i) => (
                  <g key={`star-${i}`}>
                    {/* Large colored glow */}
                    <circle
                      cx={star.x}
                      cy={star.y}
                      r="10"
                      fill={color}
                      opacity="0.5"
                    />
                    {/* Bright white star */}
                    <circle
                      cx={star.x}
                      cy={star.y}
                      r="6"
                      fill="#FFFFFF"
                      opacity="1"
                    />
                    {/* Hot white center */}
                    <circle
                      cx={star.x}
                      cy={star.y}
                      r="4"
                      fill="#FFFFFF"
                    />
                    
                    {/* 4-point star sparkle */}
                    <line x1={star.x - 14} y1={star.y} x2={star.x + 14} y2={star.y} 
                          stroke="#FFFFFF" strokeWidth="1.5" opacity="0.8" />
                    <line x1={star.x} y1={star.y - 14} x2={star.x} y2={star.y + 14} 
                          stroke="#FFFFFF" strokeWidth="1.5" opacity="0.8" />
                    
                    {/* Diagonal sparkles */}
                    <line x1={star.x - 8} y1={star.y - 8} x2={star.x + 8} y2={star.y + 8} 
                          stroke="#FFFFFF" strokeWidth="0.8" opacity="0.6" />
                    <line x1={star.x + 8} y1={star.y - 8} x2={star.x - 8} y2={star.y + 8} 
                          stroke="#FFFFFF" strokeWidth="0.8" opacity="0.6" />
                  </g>
                ))}
                
                {/* Star count text */}
                <text 
                  x="50" y="95" 
                  textAnchor="middle" 
                  fill="#FFFFFF" 
                  fontSize="5"
                  fontWeight="bold"
                  opacity="0.9"
                >
                  {expandedConstellation.stars?.length || 0} Stars
                </text>
              </svg>
              
              {/* Constellation name - prominent label */}
              <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 text-center">
                <div 
                  className="text-white text-xl sm:text-2xl font-bold px-6 py-3 rounded-xl"
                  style={{ 
                    backgroundColor: color,
                    boxShadow: `0 4px 20px ${color}`,
                  }}
                >
                  ✨ {expandedConstellation.name} ✨
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// ============ FAMILY CONSTELLATION GENERATOR ============
// Generates a unique constellation pattern based on family name
const generateFamilyConstellation = (familyName, memberCount = 1) => {
  // Use family name hash for consistent random generation
  const hash = familyName.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  
  // Seeded random number generator
  const seededRandom = (seed, index) => {
    const x = Math.sin(seed + index * 9999) * 10000;
    return x - Math.floor(x);
  };
  
  // Generate 5-8 stars based on family size
  const starCount = Math.min(8, Math.max(5, memberCount + 3));
  const stars = [];
  
  // Generate star positions in a pleasing pattern
  for (let i = 0; i < starCount; i++) {
    // Create clustered positions that form a recognizable shape
    const angle = (i / starCount) * Math.PI * 2 + seededRandom(hash, i) * 0.5;
    const radius = 25 + seededRandom(hash, i + 100) * 15;
    const x = 50 + Math.cos(angle) * radius;
    const y = 50 + Math.sin(angle) * radius;
    stars.push({ x, y, id: `family-star-${i}` });
  }
  
  // Add a center star for the family "heart"
  stars.push({ x: 50, y: 50, id: 'family-center', isCenter: true });
  
  // Generate connections to form a constellation pattern
  const connections = [];
  
  // Connect outer stars in sequence
  for (let i = 0; i < starCount - 1; i++) {
    connections.push([i, i + 1]);
  }
  connections.push([starCount - 1, 0]); // Close the outer ring
  
  // Connect some outer stars to center
  const centerIdx = stars.length - 1;
  for (let i = 0; i < starCount; i += 2) {
    connections.push([i, centerIdx]);
  }
  
  return {
    name: familyName,
    stars,
    connections,
    color: '#34D399', // Family green
    hash: Math.abs(hash)
  };
};

// Generate constellation stars from a name (for visual display)
const generateConstellationStars = (name, count = 5) => {
  const hash = name.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0);
  const stars = [];
  
  for (let i = 0; i < count; i++) {
    // Create varied positions based on hash and index
    const angle = ((hash * (i + 1)) % 360) * (Math.PI / 180);
    const radius = 15 + ((hash * (i + 3)) % 25);
    
    stars.push({
      x: 50 + Math.cos(angle) * radius,
      y: 50 + Math.sin(angle) * radius,
      size: 3 + (i % 3)
    });
  }
  
  return stars;
};

// Generate connections for constellation (simple chain with some cross-connections)
const generateConstellationConnections = (starCount) => {
  const connections = [];
  
  // Chain connections
  for (let i = 0; i < starCount - 1; i++) {
    connections.push([i, i + 1]);
  }
  
  // Add a cross-connection if enough stars
  if (starCount >= 4) {
    connections.push([0, Math.floor(starCount / 2)]);
  }
  if (starCount >= 6) {
    connections.push([1, starCount - 2]);
  }
  
  return connections;
};


// ============ FAMILY CONSTELLATION DISPLAY COMPONENT ============
const FamilyConstellationDisplay = ({ familyName, memberNames }) => {
  const constellation = useMemo(() => 
    generateFamilyConstellation(familyName, memberNames?.length || 1),
    [familyName, memberNames]
  );
  
  const [hoveredStar, setHoveredStar] = useState(null);
  
  return (
    <div className="relative w-full h-[400px] bg-gradient-to-b from-slate-900/50 to-indigo-950/30 rounded-2xl border border-emerald-500/20 overflow-hidden" data-testid="family-constellation-display">
      {/* Background stars (ambient) */}
      <div className="absolute inset-0">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-white/30 animate-pulse"
            style={{
              left: `${(constellation.hash * (i + 1)) % 100}%`,
              top: `${(constellation.hash * (i + 7)) % 100}%`,
              animationDelay: `${i * 0.2}s`
            }}
          />
        ))}
      </div>
      
      {/* Constellation lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
        <defs>
          <filter id="familyGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        {constellation.connections.map(([from, to], i) => {
          const star1 = constellation.stars[from];
          const star2 = constellation.stars[to];
          if (!star1 || !star2) return null;
          
          return (
            <line
              key={i}
              x1={`${star1.x}%`}
              y1={`${star1.y}%`}
              x2={`${star2.x}%`}
              y2={`${star2.y}%`}
              stroke={constellation.color}
              strokeWidth="2"
              strokeOpacity="0.6"
              filter="url(#familyGlow)"
              className="animate-pulse"
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          );
        })}
      </svg>
      
      {/* Stars */}
      {constellation.stars.map((star, i) => {
        const isCenter = star.isCenter;
        const memberName = memberNames?.[i] || null;
        const isHovered = hoveredStar === i;
        
        return (
          <div
            key={star.id}
            className={`absolute transition-all duration-300 cursor-pointer ${isHovered ? 'scale-150' : ''}`}
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 10
            }}
            onMouseEnter={() => setHoveredStar(i)}
            onMouseLeave={() => setHoveredStar(null)}
          >
            {/* Outer glow */}
            <div
              className={`absolute rounded-full blur-sm ${isCenter ? 'w-6 h-6 -inset-1' : 'w-4 h-4 -inset-0.5'}`}
              style={{ backgroundColor: constellation.color, opacity: isHovered ? 0.9 : 0.5 }}
            />
            {/* Core */}
            <div
              className={`relative rounded-full ${isCenter ? 'w-4 h-4' : 'w-3 h-3'} bg-white`}
            />
            
            {/* Member name tooltip */}
            {isHovered && memberName && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-slate-900/90 border border-emerald-500/30 rounded-lg whitespace-nowrap z-20">
                <span className="text-emerald-300 text-sm">{memberName}</span>
              </div>
            )}
          </div>
        );
      })}
      
      {/* Family name label */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
        <div className="px-4 py-2 bg-slate-900/80 border border-emerald-500/30 rounded-xl">
          <p className="text-emerald-400 font-philosopher font-semibold text-lg">{familyName} Family</p>
          <p className="text-slate-400 text-xs">{memberNames?.length || 0} members • {constellation.stars.length} stars</p>
        </div>
      </div>
    </div>
  );
};

// ============ ANIMATED STAR COMPONENT ============
const StarPoint = ({ star, isNew, isLit, isGoalStar, onHover, onClick, scale = 1, celebrating = false }) => {
  const [isIgniting, setIsIgniting] = useState(isNew);
  
  useEffect(() => {
    if (isNew) {
      setIsIgniting(true);
      const timer = setTimeout(() => setIsIgniting(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isNew]);
  
  const color = REALM_COLORS[star.realm] || '#818CF8';
  const baseSize = (star.size || 1) * 8 * scale;
  const size = celebrating ? baseSize * 1.5 : baseSize;
  
  // Goal stars that aren't lit yet appear faint
  const opacity = isGoalStar && !isLit ? 0.2 : 1;
  
  return (
    <div
      className={`absolute cursor-pointer transition-all duration-500 ${isIgniting ? 'animate-pulse' : ''} ${celebrating ? 'animate-bounce' : ''}`}
      style={{
        left: `${star.x}%`,
        top: `${star.y}%`,
        transform: 'translate(-50%, -50%)',
        width: size,
        height: size,
        opacity
      }}
      onMouseEnter={() => onHover && onHover(star)}
      onMouseLeave={() => onHover && onHover(null)}
      onClick={() => onClick && onClick(star)}
      data-testid={`star-${star.id || 'goal'}`}
    >
      {/* Outer glow */}
      <div
        className={`absolute inset-0 rounded-full blur-sm ${isIgniting || celebrating ? 'animate-ping' : ''}`}
        style={{ 
          backgroundColor: isLit || !isGoalStar ? color : '#64748B',
          opacity: celebrating ? 0.9 : 0.6 
        }}
      />
      {/* Core */}
      <div
        className="absolute inset-1 rounded-full"
        style={{ backgroundColor: isLit || !isGoalStar ? '#FFFFFF' : '#374151' }}
      />
      {/* Ignition burst */}
      {(isIgniting || celebrating) && (
        <div
          className="absolute inset-0 rounded-full animate-ping"
          style={{ backgroundColor: color, opacity: 0.8 }}
        />
      )}
    </div>
  );
};

// ============ ANIMATED CONSTELLATION LINES ============
const AnimatedConstellationLines = ({ stars, connections, isComplete, animate = false, color = '#818CF8' }) => {
  const [animatedLines, setAnimatedLines] = useState([]);
  
  useEffect(() => {
    if (animate && connections.length > 0) {
      // Animate lines drawing one by one
      setAnimatedLines([]);
      connections.forEach((_, i) => {
        setTimeout(() => {
          setAnimatedLines(prev => [...prev, i]);
        }, i * 300); // 300ms delay between each line
      });
    } else {
      setAnimatedLines(connections.map((_, i) => i));
    }
  }, [connections, animate]);
  
  if (!connections || connections.length === 0) return null;
  
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      {connections.map(([fromIdx, toIdx], i) => {
        const from = stars[fromIdx];
        const to = stars[toIdx];
        if (!from || !to) return null;
        
        const isAnimated = animatedLines.includes(i);
        const lineLength = Math.sqrt(Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2));
        
        return (
          <line
            key={i}
            x1={`${from.x}%`}
            y1={`${from.y}%`}
            x2={`${to.x}%`}
            y2={`${to.y}%`}
            stroke={isComplete ? color : 'rgba(100, 116, 139, 0.3)'}
            strokeWidth={isComplete ? 2 : 1}
            filter={isComplete ? 'url(#glow)' : ''}
            strokeDasharray={lineLength * 5}
            strokeDashoffset={isAnimated ? 0 : lineLength * 5}
            style={{
              transition: 'stroke-dashoffset 0.5s ease-out, stroke 0.3s ease',
            }}
          />
        );
      })}
    </svg>
  );
};

// ============ STAR HOVER TOOLTIP ============
const StarTooltip = ({ star, position }) => {
  if (!star) return null;
  
  return (
    <div
      className="fixed z-50 bg-slate-900/95 backdrop-blur-sm border border-indigo-500/30 rounded-xl p-4 shadow-2xl max-w-xs"
      style={{
        left: Math.min(position.x + 20, window.innerWidth - 280),
        top: position.y - 10,
        transform: 'translateY(-50%)'
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Star className="w-4 h-4" style={{ color: REALM_COLORS[star.realm] }} />
        <span className="text-white font-semibold">{star.lesson_title || star.name || 'Star'}</span>
      </div>
      {star.realm && (
        <p className="text-sm text-slate-400 mb-1">
          From: {REALM_NAMES[star.realm] || star.realm}
        </p>
      )}
      {star.earned_at && (
        <p className="text-xs text-slate-500">
          Earned: {new Date(star.earned_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

// ============ CONSTELLATION INFO PANEL ============
const ConstellationInfoPanel = ({ constellation, onClose, lessonsCompleted, totalLessons, remainingLessons, onNavigateToLesson }) => {
  if (!constellation) return null;
  
  const navigate = useNavigate();
  const progress = Math.round((lessonsCompleted / totalLessons) * 100);
  const isComplete = lessonsCompleted >= totalLessons;
  
  // Deduplicate lessons by both ID and title to ensure no duplicates
  const uniqueLessons = constellation.lessons ? 
    [...new Map(constellation.lessons.map(l => [l.id || l.title, l])).values()] : [];
  
  // Build a set of completed lesson IDs and titles for filtering
  const completedIds = new Set(uniqueLessons.map(l => l.id).filter(Boolean));
  const completedTitles = new Set(uniqueLessons.map(l => l.title?.toLowerCase()).filter(Boolean));
  
  // Deduplicate remaining lessons and filter out any that are already completed
  const uniqueRemainingLessons = remainingLessons ? 
    [...new Map(remainingLessons.map(l => [l.id || l.title, l])).values()]
      .filter(l => !completedIds.has(l.id) && !completedTitles.has(l.title?.toLowerCase()))
    : [];
  
  const handleContinueConstellation = () => {
    if (uniqueRemainingLessons && uniqueRemainingLessons.length > 0) {
      // Navigate to the first remaining lesson
      navigate(`/lesson-player/${uniqueRemainingLessons[0].id}`);
      onClose();
    }
  };
  
  const handleLessonClick = (lessonId) => {
    if (lessonId) {
      navigate(`/lesson-player/${lessonId}`);
      onClose();
    }
  };
  
  // Generate or use existing constellation stars for visual
  const constellationStars = constellation.stars || generateConstellationStars(constellation.name, totalLessons || 5);
  const constellationConnections = constellation.connections || generateConstellationConnections(constellationStars.length);
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="glass-panel p-6 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            {isComplete ? (
              <Trophy className="w-8 h-8 text-amber-400" />
            ) : (
              <Star className="w-8 h-8 text-indigo-400" />
            )}
            <div>
              <h2 className="text-xl font-philosopher font-bold text-white">{constellation.name}</h2>
              <p className="text-sm text-slate-400">{REALM_NAMES[constellation.realm] || constellation.creator_name || 'Explorer Sky'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Constellation Visual Preview */}
        <div className="relative h-48 bg-gradient-to-b from-slate-900 to-indigo-950/50 rounded-xl mb-4 overflow-hidden border border-slate-700/50">
          {/* Background stars */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={`bg-star-${i}`}
                className="absolute w-1 h-1 bg-white/30 rounded-full"
                style={{
                  left: `${(i * 37) % 100}%`,
                  top: `${(i * 53) % 100}%`,
                  animation: `twinkle ${2 + (i % 3)}s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`
                }}
              />
            ))}
          </div>
          
          {/* Constellation lines and stars */}
          <svg className="absolute inset-0 w-full h-full">
            {/* Connections */}
            {constellationConnections.map(([from, to], i) => {
              const star1 = constellationStars[from];
              const star2 = constellationStars[to];
              if (!star1 || !star2) return null;
              const x1 = typeof star1.x === 'number' ? star1.x : parseFloat(star1.x);
              const y1 = typeof star1.y === 'number' ? star1.y : parseFloat(star1.y);
              const x2 = typeof star2.x === 'number' ? star2.x : parseFloat(star2.x);
              const y2 = typeof star2.y === 'number' ? star2.y : parseFloat(star2.y);
              const isLit = i < lessonsCompleted;
              
              return (
                <line
                  key={`line-${i}`}
                  x1={`${x1}%`}
                  y1={`${y1}%`}
                  x2={`${x2}%`}
                  y2={`${y2}%`}
                  stroke={isLit ? (isComplete ? '#F59E0B' : '#818CF8') : '#334155'}
                  strokeWidth="2"
                  strokeLinecap="round"
                  opacity={isLit ? 0.8 : 0.3}
                />
              );
            })}
            
            {/* Stars */}
            {constellationStars.map((star, i) => {
              const x = typeof star.x === 'number' ? star.x : parseFloat(star.x);
              const y = typeof star.y === 'number' ? star.y : parseFloat(star.y);
              const isLit = i < lessonsCompleted;
              
              return (
                <g key={`star-${i}`}>
                  {/* Glow */}
                  {isLit && (
                    <circle
                      cx={`${x}%`}
                      cy={`${y}%`}
                      r="8"
                      fill={isComplete ? '#F59E0B' : '#818CF8'}
                      opacity="0.3"
                    />
                  )}
                  {/* Star */}
                  <circle
                    cx={`${x}%`}
                    cy={`${y}%`}
                    r="4"
                    fill={isLit ? (isComplete ? '#FCD34D' : '#A5B4FC') : '#64748B'}
                  />
                </g>
              );
            })}
          </svg>
          
          {/* Constellation name overlay */}
          <div className="absolute bottom-2 left-2 right-2 text-center">
            <span className={`text-sm font-philosopher ${isComplete ? 'text-amber-400' : 'text-indigo-400'}`}>
              {constellation.name}
            </span>
          </div>
        </div>
        
        {/* Story */}
        {constellation.story && (
          <div className="bg-slate-900/50 p-4 rounded-xl mb-4">
            <p className="text-slate-300 italic">"{constellation.story}"</p>
          </div>
        )}
        
        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-400">Stars Lit</span>
            <span className="text-white font-semibold">{lessonsCompleted} / {totalLessons}</span>
          </div>
          <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${isComplete ? 'bg-amber-400' : 'bg-indigo-500'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        {/* Contributing lessons (deduplicated) */}
        {uniqueLessons.length > 0 && (
          <div className="bg-slate-900/50 p-4 rounded-xl mb-4">
            <p className="text-sm text-green-400 mb-2">
              <Check className="w-4 h-4 inline mr-1" />
              Completed ({uniqueLessons.length} lessons):
            </p>
            <ul className="text-sm text-slate-300 space-y-1 max-h-24 overflow-y-auto">
              {uniqueLessons.map((lesson, i) => (
                <li key={lesson.id || i} className="flex items-center gap-2">
                  <Star className="w-3 h-3 flex-shrink-0 text-green-400" />
                  <span className="truncate">{lesson.title}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Remaining lessons needed */}
        {!isComplete && uniqueRemainingLessons && uniqueRemainingLessons.length > 0 && (
          <div className="bg-indigo-900/20 border border-indigo-500/30 p-4 rounded-xl mb-4">
            <p className="text-sm text-indigo-400 mb-2">
              <Sparkles className="w-4 h-4 inline mr-1" />
              Still needed ({uniqueRemainingLessons.length} lessons):
            </p>
            <ul className="text-sm text-slate-300 space-y-2 max-h-32 overflow-y-auto">
              {uniqueRemainingLessons.map((lesson, i) => (
                <li key={lesson.id || i}>
                  <button
                    onClick={() => handleLessonClick(lesson.id)}
                    className="flex items-center gap-2 w-full text-left hover:text-indigo-300 transition-colors group"
                  >
                    <div className="w-3 h-3 rounded-full border border-slate-500 flex-shrink-0 group-hover:border-indigo-400" />
                    <span className="truncate group-hover:underline">{lesson.title}</span>
                    <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Complete badge */}
        {isComplete ? (
          <div className="p-3 bg-amber-500/20 border border-amber-500/30 rounded-xl text-center">
            <Trophy className="w-6 h-6 text-amber-400 mx-auto mb-1" />
            <p className="text-amber-300 font-semibold">Constellation Complete!</p>
          </div>
        ) : (
          /* Continue Constellation CTA */
          uniqueRemainingLessons && uniqueRemainingLessons.length > 0 && (
            <button
              onClick={handleContinueConstellation}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl px-6 py-3 font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2"
              data-testid="continue-constellation-btn"
            >
              <Sparkles className="w-5 h-5" />
              Continue This Constellation
            </button>
          )
        )}
      </div>
    </div>
  );
};

// ============ CELEBRATION OVERLAY with CONSTELLATION UNLOCK ANIMATION ============
const CelebrationOverlay = ({ constellation, onComplete }) => {
  const [animationPhase, setAnimationPhase] = useState(0);
  const [lineProgress, setLineProgress] = useState(0);
  
  useEffect(() => {
    // Phase 0: Star ignition burst (0-500ms)
    // Phase 1: Line drawing animation (500-1500ms)
    // Phase 2: Ripple effect and celebration (1500-3000ms)
    
    const phase1Timer = setTimeout(() => setAnimationPhase(1), 500);
    const phase2Timer = setTimeout(() => setAnimationPhase(2), 1500);
    const completeTimer = setTimeout(onComplete, 3500);
    
    // Animate line drawing
    const lineInterval = setInterval(() => {
      setLineProgress(prev => Math.min(prev + 5, 100));
    }, 50);
    
    return () => {
      clearTimeout(phase1Timer);
      clearTimeout(phase2Timer);
      clearTimeout(completeTimer);
      clearInterval(lineInterval);
    };
  }, [onComplete]);
  
  // Get constellation pattern for line drawing
  const stars = constellation?.stars || CONSTELLATION_GOALS[constellation?.realm]?.stars || [];
  const connections = constellation?.connections || CONSTELLATION_GOALS[constellation?.realm]?.connections || [];
  
  return (
    <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50">
      {/* Ripple waves - Phase 2 */}
      {animationPhase >= 2 && (
        <>
          {[...Array(3)].map((_, i) => (
            <div
              key={`ripple-${i}`}
              className="absolute rounded-full border-2 border-amber-400/30"
              style={{
                width: 100 + i * 150,
                height: 100 + i * 150,
                animation: `ripple-expand 1.5s ease-out ${i * 0.3}s`,
                animationFillMode: 'both'
              }}
            />
          ))}
        </>
      )}
      
      <div className="text-center relative">
        {/* Constellation visualization with animations */}
        <div className="relative w-64 h-64 mx-auto mb-8">
          {/* Star ignition burst - Phase 0 */}
          {animationPhase === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 rounded-full bg-amber-400/50 animate-ping" />
              <div className="absolute w-24 h-24 rounded-full bg-amber-300/70 animate-ping" style={{ animationDelay: '0.1s' }} />
              <div className="absolute w-16 h-16 rounded-full bg-white animate-ping" style={{ animationDelay: '0.2s' }} />
            </div>
          )}
          
          {/* Constellation lines drawing - Phase 1+ */}
          {animationPhase >= 1 && stars.length > 0 && (
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
              <defs>
                <filter id="constellation-glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FBBF24" stopOpacity="1" />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity="1" />
                </linearGradient>
              </defs>
              
              {/* Animated lines */}
              {connections.map(([startIdx, endIdx], i) => {
                const start = stars[startIdx];
                const end = stars[endIdx];
                if (!start || !end) return null;
                
                const lineStart = (i / connections.length) * 100;
                const lineEnd = ((i + 1) / connections.length) * 100;
                const isDrawn = lineProgress >= lineEnd;
                const isDrawing = lineProgress > lineStart && lineProgress < lineEnd;
                const drawProgress = isDrawing ? (lineProgress - lineStart) / (lineEnd - lineStart) : 1;
                
                if (!isDrawn && !isDrawing) return null;
                
                return (
                  <line
                    key={`line-${i}`}
                    x1={start.x}
                    y1={start.y}
                    x2={isDrawing ? start.x + (end.x - start.x) * drawProgress : end.x}
                    y2={isDrawing ? start.y + (end.y - start.y) * drawProgress : end.y}
                    stroke="url(#line-gradient)"
                    strokeWidth="2"
                    filter="url(#constellation-glow)"
                    className={isDrawn ? 'animate-pulse' : ''}
                  />
                );
              })}
              
              {/* Stars that light up */}
              {stars.map((star, i) => {
                const starProgress = (i / stars.length) * 100;
                const isLit = lineProgress >= starProgress;
                
                return (
                  <g key={`star-${i}`}>
                    {isLit && (
                      <>
                        <circle
                          cx={star.x}
                          cy={star.y}
                          r="4"
                          fill="#FBBF24"
                          filter="url(#constellation-glow)"
                          className="animate-pulse"
                        />
                        <circle
                          cx={star.x}
                          cy={star.y}
                          r="2"
                          fill="white"
                        />
                      </>
                    )}
                  </g>
                );
              })}
            </svg>
          )}
          
          {/* Trophy icon appears after ripple */}
          {animationPhase >= 2 && (
            <div className="absolute inset-0 flex items-center justify-center animate-bounce-in">
              <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-full p-4 shadow-lg shadow-amber-500/50">
                <Trophy className="w-12 h-12 text-white" />
              </div>
            </div>
          )}
        </div>
        
        {/* Celebration particles - Phase 2 */}
        {animationPhase >= 2 && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={`particle-${i}`}
                className="absolute w-2 h-2 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  backgroundColor: ['#FBBF24', '#F59E0B', '#FDE68A', '#FFFFFF'][i % 4],
                  animation: `float-particle 2s ease-out ${i * 0.05}s`,
                  animationFillMode: 'both'
                }}
              />
            ))}
          </div>
        )}
        
        {/* Text content */}
        <h2 
          className={`text-4xl font-philosopher font-bold text-white mb-3 transition-all duration-500 ${
            animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Constellation Complete!
        </h2>
        <p 
          className={`text-2xl text-amber-400 font-semibold mb-4 transition-all duration-500 delay-100 ${
            animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {constellation.name}
        </p>
        <p 
          className={`text-slate-400 max-w-md mx-auto transition-all duration-500 delay-200 ${
            animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {constellation.story}
        </p>
      </div>
      
      {/* Animation keyframes */}
      <style>{`
        @keyframes ripple-expand {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(3);
            opacity: 0;
          }
        }
        @keyframes bounce-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes float-particle {
          0% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateY(-100px) scale(0);
            opacity: 0;
          }
        }
        @keyframes constellationReveal {
          0% {
            transform: scale(0.3);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

// ============ MAIN TEMPLE SKY PAGE ============
const TempleSkyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeLayer, setActiveLayer] = useState('personal');
  const [learningStars, setLearningStars] = useState([]);
  const [constellationGoals, setConstellationGoals] = useState({});
  const [constellations, setConstellations] = useState([]);
  const [remainingLessonsMap, setRemainingLessonsMap] = useState({}); // Remaining lessons per realm
  const [familyMembers, setFamilyMembers] = useState([]); // Family member names for constellation
  const [customConstellations, setCustomConstellations] = useState([]); // Child's saved custom constellations
  const [loading, setLoading] = useState(true);
  const [selectedConstellation, setSelectedConstellation] = useState(null);
  const [hoveredStar, setHoveredStar] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [newStarId, setNewStarId] = useState(null);
  const [newStarRealm, setNewStarRealm] = useState(null); // Realm of newly earned star (for ignition animation)
  const [zoom, setZoom] = useState(1);
  const [celebrating, setCelebrating] = useState(null);
  const [animateNewConnection, setAnimateNewConnection] = useState(false);
  const [view3D, setView3D] = useState(true); // Default to 3D planetarium view
  const [selected3DConstellation, setSelected3DConstellation] = useState(null);
  const [selectedCustomConstellation, setSelectedCustomConstellation] = useState(null); // Selected custom constellation
  const [expandedConstellationId, setExpandedConstellationId] = useState(null); // Track which custom constellation is expanded
  
  // Handler for custom constellation click - toggles expansion and selects for info panel
  const handleCustomConstellationClick = (constellation) => {
    if (expandedConstellationId === constellation.id) {
      // Already expanded - collapse it
      setExpandedConstellationId(null);
      setSelectedCustomConstellation(null);
    } else {
      // Expand this constellation
      setExpandedConstellationId(constellation.id);
      setSelectedCustomConstellation(constellation);
    }
  };
  
  const [selectedChild, setSelectedChild] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('selectedChild') || 'null');
    } catch {
      return null;
    }
  });
  
  // ALWAYS re-read child from localStorage on mount and when navigating back
  // This ensures we have the correct child even if localStorage was updated elsewhere
  useEffect(() => {
    const readChildFromStorage = () => {
      try {
        const storedChild = JSON.parse(localStorage.getItem('selectedChild') || 'null');
        if (storedChild) {
          console.log('Temple Sky: Loading child from localStorage:', storedChild.name, storedChild.id);
          setSelectedChild(storedChild);
        }
      } catch (err) {
        console.error('Error reading selectedChild from localStorage:', err);
      }
    };
    
    // Read immediately on mount
    readChildFromStorage();
    
    // Also listen for storage changes (e.g., when child is selected in another tab)
    window.addEventListener('storage', readChildFromStorage);
    
    // Re-read when page becomes visible again (handles browser back/forward)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        readChildFromStorage();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('storage', readChildFromStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Check for newly completed lesson
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newStar = params.get('newStar');
    const newStarRealmParam = params.get('realm'); // e.g., star_map, memory_garden, etc.
    
    if (newStar) {
      setNewStarId(newStar);
      setAnimateNewConnection(true);
      
      // Map realm to journey constellation ID for the 3D ignition effect
      const REALM_TO_JOURNEY = {
        'star_map': 'ancient_worlds',
        'memory_garden': 'coding_logic',
        'creative_forge': 'creativity_music',
        'radiant_will': 'strategy_chess',
        'calm_cove': 'nature_science'
      };
      
      if (newStarRealmParam && REALM_TO_JOURNEY[newStarRealmParam]) {
        setNewStarRealm(REALM_TO_JOURNEY[newStarRealmParam]);
      }
      
      // Clear the animation state after it completes
      setTimeout(() => {
        setNewStarId(null);
        setAnimateNewConnection(false);
        // Clear URL params without reload
        window.history.replaceState({}, '', location.pathname);
      }, 3000);
    }
  }, [location]);
  
  useEffect(() => {
    if (!selectedChild && activeLayer !== 'community') {
      navigate('/child-select');
      return;
    }
    
    // Log the child we're loading data for
    if (selectedChild) {
      console.log('Temple Sky: Loading data for child:', selectedChild.name, selectedChild.id);
    }
    
    loadSkyData();
  }, [selectedChild?.id, activeLayer]); // Use selectedChild.id to detect actual child changes
  
  const loadSkyData = async () => {
    setLoading(true);
    
    // Clear ALL previous data to prevent stale state across child switches
    setLearningStars([]);
    setConstellationGoals({});
    setConstellations([]);
    setCustomConstellations([]);
    setFamilyMembers([]);
    setSelectedConstellation(null);
    setExpandedConstellationId(null);
    
    try {
      if (activeLayer === 'personal' && selectedChild) {
        // Load learning stars from completed lessons
        try {
          const completionsRes = await progressAPI.getCompletions(selectedChild.id);
          const completions = completionsRes.data || [];
          
          // Group completions by realm
          const realmCompletions = {};
          completions.forEach(c => {
            const realm = c.realm || 'star_map';
            if (!realmCompletions[realm]) realmCompletions[realm] = [];
            realmCompletions[realm].push(c);
          });
          
          // Build constellation goals progress
          const goals = {};
          Object.entries(CONSTELLATION_GOALS).forEach(([realm, goalDef]) => {
            const realmLessons = realmCompletions[realm] || [];
            const litCount = Math.min(realmLessons.length, goalDef.stars.length);
            const isComplete = litCount >= goalDef.stars.length;
            
            goals[realm] = {
              ...goalDef,
              realm,
              litStars: litCount,
              isComplete,
              lessons: realmLessons.slice(0, goalDef.stars.length).map(l => ({
                title: l.lesson_title,
                realm: l.realm
              }))
            };
          });
          setConstellationGoals(goals);
          
          // Transform completions into display stars (positioned on goal constellation)
          const stars = [];
          Object.entries(realmCompletions).forEach(([realm, lessons]) => {
            const goalDef = CONSTELLATION_GOALS[realm];
            if (goalDef) {
              lessons.slice(0, goalDef.stars.length).forEach((lesson, i) => {
                if (goalDef.stars[i]) {
                  stars.push({
                    id: lesson.lesson_id,
                    lesson_title: lesson.lesson_title,
                    realm,
                    x: goalDef.stars[i].x,
                    y: goalDef.stars[i].y,
                    size: 1.2,
                    earned_at: lesson.completed_at,
                    goalIndex: i
                  });
                }
              });
            }
          });
          setLearningStars(stars);
          
          // Check if any constellation just completed
          Object.values(goals).forEach(goal => {
            if (goal.isComplete && goal.litStars === goal.stars.length) {
              const wasJustCompleted = localStorage.getItem(`constellation_celebrated_${goal.realm}`);
              if (!wasJustCompleted) {
                setCelebrating(goal);
                localStorage.setItem(`constellation_celebrated_${goal.realm}`, 'true');
              }
            }
          });
          
          // Fetch remaining lessons for each realm
          const remainingMap = {};
          for (const realm of Object.keys(CONSTELLATION_GOALS)) {
            if (!goals[realm]?.isComplete) {
              try {
                const lessonsRes = await api.get(`/lessons/published/${realm}`);
                const allLessons = lessonsRes.data || [];
                const completedIds = new Set((realmCompletions[realm] || []).map(c => c.lesson_id));
                const remaining = allLessons
                  .filter(l => !completedIds.has(l.id))
                  .slice(0, CONSTELLATION_GOALS[realm].stars.length - (goals[realm]?.litStars || 0));
                remainingMap[realm] = remaining;
              } catch (err) {
                remainingMap[realm] = [];
              }
            }
          }
          setRemainingLessonsMap(remainingMap);
          
        } catch (err) {
          console.log('Could not load learning stars:', err);
          setLearningStars([]);
          setConstellationGoals({});
        }
        
        // Load custom constellations created by the child
        try {
          const customRes = await constellationsAPI.getAll(selectedChild.id);
          setCustomConstellations(customRes.data || []);
        } catch (err) {
          console.log('Could not load custom constellations:', err);
          setCustomConstellations([]);
        }
        
        // Load custom constellations from sky API (community shared)
        try {
          const skyRes = await skyAPI.getPersonal(selectedChild.id);
          setConstellations(skyRes.data?.constellations || []);
        } catch (err) {
          setConstellations([]);
        }
      } else if (activeLayer === 'family' && selectedChild) {
        console.log('Temple Sky Family: Loading family sky for', selectedChild.name, selectedChild.id);
        const response = await skyAPI.getFamily(selectedChild.id);
        setConstellations(response.data?.constellations || []);
        setLearningStars([]);
        setConstellationGoals({});
        
        // Load family members (siblings) for family constellation
        // Put the selected child first in the list
        try {
          const childrenRes = await api.get('/children');
          const children = childrenRes.data || [];
          // Sort so selected child is first, then others alphabetically
          const sortedChildren = children.sort((a, b) => {
            if (a.id === selectedChild.id) return -1;
            if (b.id === selectedChild.id) return 1;
            return (a.name || '').localeCompare(b.name || '');
          });
          setFamilyMembers(sortedChildren.map(c => c.name));
          console.log('Temple Sky Family: Family members (sorted):', sortedChildren.map(c => c.name));
        } catch (err) {
          setFamilyMembers([selectedChild.name]);
        }
      } else if (activeLayer === 'community') {
        const response = await skyAPI.getCommunity(50);
        setConstellations(response.data?.constellations || []);
        setLearningStars([]);
        setConstellationGoals({});
      }
    } catch (error) {
      console.error('Error loading sky:', error);
      toast.error('Failed to load sky');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMouseMove = (e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };
  
  const handleStarClick = (star) => {
    if (star.realm && constellationGoals[star.realm]) {
      setSelectedConstellation({
        ...constellationGoals[star.realm],
        lessonsCompleted: constellationGoals[star.realm].litStars,
        totalLessons: constellationGoals[star.realm].stars.length
      });
    }
  };
  
  const handleConstellationGoalClick = (realm) => {
    const goal = constellationGoals[realm];
    if (goal) {
      setSelectedConstellation({
        ...goal,
        lessonsCompleted: goal.litStars,
        totalLessons: goal.stars.length
      });
    }
  };
  
  const activeLayerInfo = SKY_LAYERS.find(l => l.id === activeLayer);
  const totalStars = learningStars.length;
  const completedConstellations = Object.values(constellationGoals).filter(g => g.isComplete).length;
  
  return (
    <div className="min-h-screen bg-[#0a0a1a] relative overflow-hidden" onMouseMove={handleMouseMove}>
      {/* Animated deep space background with varied stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Small distant stars */}
        {[...Array(150)].map((_, i) => (
          <div
            key={`small-${i}`}
            className="absolute w-0.5 h-0.5 bg-white/40 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
        {/* Medium stars */}
        {[...Array(50)].map((_, i) => (
          <div
            key={`med-${i}`}
            className="absolute w-1 h-1 bg-white/60 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
        {/* Bright stars */}
        {[...Array(20)].map((_, i) => (
          <div
            key={`bright-${i}`}
            className="absolute w-1.5 h-1.5 bg-white rounded-full shadow-lg shadow-white/50"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkleBright ${2 + Math.random() * 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
        {/* Nebula glows */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-900/20 rounded-full blur-3xl animate-drift" />
        <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-purple-900/20 rounded-full blur-3xl animate-drift-slow" />
        <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-pink-900/10 rounded-full blur-3xl animate-drift" />
      </div>
      
      <div className="relative z-10 pt-safe pb-24 sm:pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header - with mobile safe area */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6 pt-4 sm:pt-0">
            <button
              onClick={() => navigate('/temple')}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors self-start"
              data-testid="back-to-temple-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Temple</span>
            </button>
            
            <div className="flex items-center gap-3 sm:gap-4 self-end sm:self-auto">
              {/* 3D/2D View Toggle */}
              <button
                onClick={() => setView3D(!view3D)}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  view3D 
                    ? 'bg-indigo-500/20 border border-indigo-500/50 text-indigo-300' 
                    : 'bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white'
                }`}
                title={view3D ? 'Switch to 2D view' : 'Switch to 3D Planetarium'}
                data-testid="view-toggle-btn"
              >
                <Globe className="w-4 h-4" />
              </button>
              
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => setZoom(z => Math.min(1.5, z + 0.1))}
                  className="p-1.5 sm:p-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg text-slate-400 hover:text-white transition-colors"
                  disabled={view3D}
                  style={{ opacity: view3D ? 0.5 : 1 }}
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setZoom(z => Math.max(0.7, z - 0.1))}
                  className="p-1.5 sm:p-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg text-slate-400 hover:text-white transition-colors"
                  disabled={view3D}
                  style={{ opacity: view3D ? 0.5 : 1 }}
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 sm:w-5 h-4 sm:h-5 text-amber-400" />
                <span className="text-white font-philosopher text-sm sm:text-base">Temple Sky</span>
              </div>
            </div>
          </div>
          
          {/* Title and Stats */}
          <div className="text-center mb-4 sm:mb-6">
            <h1 className="text-2xl sm:text-4xl font-philosopher font-bold text-white mb-2">
              {activeLayerInfo?.name || 'Temple Sky'}
            </h1>
            <p className="text-slate-400 text-sm sm:text-base">{activeLayerInfo?.description}</p>
            
            {/* Show current child name prominently */}
            {selectedChild && (
              <div className="mt-2 text-sm text-indigo-300">
                Viewing as: <span className="font-semibold text-white">{selectedChild.name}</span>
              </div>
            )}
            
            {selectedChild && activeLayer === 'personal' && (
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-3">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-300 text-sm">{totalStars} stars earned</span>
                </div>
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-indigo-400" />
                  <span className="text-indigo-300 text-sm">{completedConstellations}/5 constellations</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Sky Layer Tabs - scrollable on mobile */}
          <div className="flex justify-start sm:justify-center gap-2 sm:gap-4 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {SKY_LAYERS.map(layer => {
              const Icon = layer.icon;
              const isActive = activeLayer === layer.id;
              
              return (
                <button
                  key={layer.id}
                  onClick={() => setActiveLayer(layer.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-white/10 border border-white/20 text-white' 
                      : 'bg-slate-900/50 border border-slate-800/50 text-slate-400 hover:text-white hover:border-slate-700'
                  }`}
                  style={isActive ? { borderColor: `${layer.color}50`, boxShadow: `0 0 20px ${layer.color}20` } : {}}
                  data-testid={`sky-tab-${layer.id}`}
                >
                  <Icon className="w-4 sm:w-5 h-4 sm:h-5" style={isActive ? { color: layer.color } : {}} />
                  <span className="font-medium text-sm sm:text-base whitespace-nowrap">{layer.name}</span>
                </button>
              );
            })}
          </div>
          
          {/* Loading state */}
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin w-10 h-10 border-2 border-white/20 border-t-indigo-500 rounded-full mx-auto mb-4"></div>
              <p className="text-slate-400">Loading the sky...</p>
            </div>
          ) : activeLayer === 'personal' ? (
            /* Personal Sky with Constellation Goals */
            <div className="space-y-4 sm:space-y-6">
              {/* 3D Planetarium View */}
              {view3D ? (
                <div className="space-y-4">
                  <SkyDome3D 
                    constellationProgress={{
                      ancient_worlds: (constellationGoals.star_map?.litStars || 0) / (constellationGoals.star_map?.stars?.length || 5),
                      creativity_music: (constellationGoals.creative_forge?.litStars || 0) / (constellationGoals.creative_forge?.stars?.length || 8),
                      coding_logic: (constellationGoals.memory_garden?.litStars || 0) / (constellationGoals.memory_garden?.stars?.length || 5),
                      strategy_chess: (constellationGoals.radiant_will?.litStars || 0) / (constellationGoals.radiant_will?.stars?.length || 8),
                      nature_science: (constellationGoals.calm_cove?.litStars || 0) / (constellationGoals.calm_cove?.stars?.length || 7)
                    }}
                    customConstellations={customConstellations}
                    onSelectConstellation={setSelected3DConstellation}
                    onSelectCustomConstellation={handleCustomConstellationClick}
                    selectedConstellation={selected3DConstellation}
                    newStarRealm={newStarRealm}
                    onIgnitionComplete={() => setNewStarRealm(null)}
                    childId={selectedChild?.id}
                    selectedConstellationId={selected3DConstellation?.id}
                    expandedConstellationId={expandedConstellationId}
                  />
                  
                  {/* Selected Custom Constellation Info Panel */}
                  {selectedCustomConstellation && (
                    <div className="glass-panel p-4 sm:p-6 rounded-2xl border border-purple-500/30">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
                            <Star className="w-5 h-5 text-purple-400" />
                            {selectedCustomConstellation.name}
                          </h3>
                          <p className="text-slate-400 text-sm mt-1">Your Personal Creation</p>
                        </div>
                        <button 
                          onClick={() => {
                            setSelectedCustomConstellation(null);
                            setExpandedConstellationId(null);
                          }}
                          className="text-slate-400 hover:text-white"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      {/* Constellation Visual Preview */}
                      <div className="mb-4 p-4 bg-slate-900/60 rounded-xl border border-purple-500/20 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent" />
                        <svg viewBox="0 0 100 100" className="w-full h-40 relative z-10">
                          {/* Connection lines */}
                          {selectedCustomConstellation.connections?.map(([fromIdx, toIdx], i) => {
                            const fromStar = selectedCustomConstellation.stars?.[fromIdx];
                            const toStar = selectedCustomConstellation.stars?.[toIdx];
                            if (!fromStar || !toStar) return null;
                            
                            // Generate color from constellation name
                            const hash = selectedCustomConstellation.name.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0);
                            const hue = Math.abs(hash) % 360;
                            
                            return (
                              <line
                                key={`line-${i}`}
                                x1={fromStar.x}
                                y1={fromStar.y}
                                x2={toStar.x}
                                y2={toStar.y}
                                stroke={`hsl(${hue}, 70%, 65%)`}
                                strokeWidth="1.5"
                                opacity="0.8"
                                className="animate-pulse"
                                style={{ animationDelay: `${i * 0.1}s` }}
                              />
                            );
                          })}
                          
                          {/* Stars */}
                          {selectedCustomConstellation.stars?.map((star, i) => {
                            const hash = selectedCustomConstellation.name.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0);
                            const hue = Math.abs(hash) % 360;
                            
                            return (
                              <g key={`star-${i}`}>
                                {/* Outer glow */}
                                <circle
                                  cx={star.x}
                                  cy={star.y}
                                  r="6"
                                  fill={`hsl(${hue}, 70%, 65%)`}
                                  opacity="0.3"
                                />
                                {/* Inner star */}
                                <circle
                                  cx={star.x}
                                  cy={star.y}
                                  r="3"
                                  fill="#FFFFFF"
                                  className="animate-pulse"
                                  style={{ animationDelay: `${i * 0.15}s` }}
                                />
                              </g>
                            );
                          })}
                        </svg>
                        <p className="text-center text-xs text-slate-500 mt-2">Your constellation pattern</p>
                      </div>
                      
                      {selectedCustomConstellation.story && (
                        <div className="mb-4 p-3 bg-purple-500/10 rounded-xl border border-purple-500/20">
                          <p className="text-purple-200 text-sm italic">"{selectedCustomConstellation.story}"</p>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-slate-400 mb-4">
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4" />
                          {selectedCustomConstellation.stars?.length || 0} Stars
                        </span>
                        <span>•</span>
                        <span>Created {new Date(selectedCustomConstellation.created_at).toLocaleDateString()}</span>
                      </div>
                      
                      <button
                        onClick={() => navigate('/courtyard')}
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl px-6 py-3 font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2"
                        data-testid="view-in-courtyard-btn"
                      >
                        <Sparkles className="w-5 h-5" />
                        Create More Constellations
                      </button>
                    </div>
                  )}
                  
                  {/* Selected Journey Constellation Info Panel */}
                  {selected3DConstellation && (() => {
                    // Get realm info for this constellation
                    const realmId = selected3DConstellation.id === 'ancient_worlds' ? 'star_map' :
                                   selected3DConstellation.id === 'creativity_music' ? 'creative_forge' :
                                   selected3DConstellation.id === 'coding_logic' ? 'memory_garden' :
                                   selected3DConstellation.id === 'strategy_chess' ? 'radiant_will' : 'calm_cove';
                    const goalData = constellationGoals[realmId] || {};
                    const litStars = goalData.litStars || 0;
                    const totalStars = goalData.stars?.length || 5;
                    const pattern = JOURNEY_CONSTELLATION_PATTERNS[selected3DConstellation.id];
                    
                    return (
                      <div className="glass-panel p-4 sm:p-6 rounded-2xl border border-indigo-500/30">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
                              <span 
                                className="w-4 h-4 rounded-full" 
                                style={{ backgroundColor: selected3DConstellation.color }}
                              />
                              {selected3DConstellation.name}
                            </h3>
                            <p className="text-slate-400 text-sm mt-1">{selected3DConstellation.description}</p>
                          </div>
                          <button 
                            onClick={() => setSelected3DConstellation(null)}
                            className="text-slate-400 hover:text-white"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        
                        {/* 2D Constellation Diagram */}
                        <div className="mb-4 p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                          <div className="text-center mb-3">
                            <span className="text-xs text-slate-500 uppercase tracking-wider">Your Constellation</span>
                          </div>
                          <svg viewBox="0 0 100 100" className="w-full h-32 sm:h-40">
                            {/* Constellation lines - only connect lit stars */}
                            {pattern?.connections?.map(([fromIdx, toIdx], i) => {
                              const fromStar = pattern.stars[fromIdx];
                              const toStar = pattern.stars[toIdx];
                              if (!fromStar || !toStar) return null;
                              
                              // Convert polar to 2D coords for display
                              const fromX = 50 + Math.cos(fromStar.angle) * fromStar.distance * 15;
                              const fromY = 50 - fromStar.height * 3;
                              const toX = 50 + Math.cos(toStar.angle) * toStar.distance * 15;
                              const toY = 50 - toStar.height * 3;
                              
                              const fromLit = fromIdx < litStars;
                              const toLit = toIdx < litStars;
                              const lineVisible = fromLit && toLit;
                              
                              return (
                                <line
                                  key={`line-${i}`}
                                  x1={fromX}
                                  y1={fromY}
                                  x2={toX}
                                  y2={toY}
                                  stroke={lineVisible ? selected3DConstellation.color : '#334155'}
                                  strokeWidth={lineVisible ? 1.5 : 0.5}
                                  strokeDasharray={lineVisible ? 'none' : '2,2'}
                                  opacity={lineVisible ? 0.8 : 0.3}
                                />
                              );
                            })}
                            
                            {/* Stars */}
                            {pattern?.stars?.map((star, i) => {
                              const x = 50 + Math.cos(star.angle) * star.distance * 15;
                              const y = 50 - star.height * 3;
                              const isLit = i < litStars;
                              
                              return (
                                <g key={`star-${i}`}>
                                  {/* Outer glow for lit stars */}
                                  {isLit && (
                                    <circle
                                      cx={x}
                                      cy={y}
                                      r={6}
                                      fill={selected3DConstellation.color}
                                      opacity={0.3}
                                    />
                                  )}
                                  {/* Star core */}
                                  <circle
                                    cx={x}
                                    cy={y}
                                    r={isLit ? 4 : 3}
                                    fill={isLit ? '#FFFFFF' : 'transparent'}
                                    stroke={isLit ? selected3DConstellation.color : '#475569'}
                                    strokeWidth={isLit ? 1 : 1.5}
                                    strokeDasharray={isLit ? 'none' : '2,2'}
                                  />
                                  {/* Inner highlight for lit stars */}
                                  {isLit && (
                                    <circle
                                      cx={x}
                                      cy={y}
                                      r={2}
                                      fill={selected3DConstellation.color}
                                    />
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                          
                          {/* Legend */}
                          <div className="flex justify-center gap-6 mt-3 text-xs">
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 rounded-full bg-white border-2" style={{ borderColor: selected3DConstellation.color }} />
                              <span className="text-slate-400">Earned ({litStars})</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 rounded-full border-2 border-dashed border-slate-500" />
                              <span className="text-slate-500">Not Yet ({totalStars - litStars})</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-400">Journey Progress</span>
                            <span className="text-white font-semibold">
                              {litStars}/{totalStars} Stars • {Math.round((litStars / totalStars) * 100)}%
                            </span>
                          </div>
                          <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full transition-all"
                              style={{ 
                                backgroundColor: selected3DConstellation.color,
                                width: `${(litStars / totalStars) * 100}%`
                              }}
                            />
                          </div>
                        </div>
                        
                        <button
                          onClick={() => navigate('/journey-selection')}
                          className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl px-6 py-3 font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2"
                          data-testid="explore-journey-btn"
                        >
                          <Sparkles className="w-5 h-5" />
                          Explore This Journey
                        </button>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Original 2D View */
                <>
                  {/* Constellation Goal Cards - scrollable on mobile */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 mb-4 sm:mb-6">
                    {Object.entries(constellationGoals).map(([realm, goal]) => (
                      <button
                        key={realm}
                        onClick={() => handleConstellationGoalClick(realm)}
                        className={`p-3 sm:p-4 rounded-xl border transition-all hover:scale-105 ${
                          goal.isComplete 
                            ? 'bg-amber-500/20 border-amber-500/50' 
                            : 'bg-slate-900/50 border-slate-700/50 hover:border-indigo-500/50'
                        }`}
                        data-testid={`constellation-goal-${realm}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: REALM_COLORS[realm] }} />
                          {goal.isComplete && <Trophy className="w-4 h-4 text-amber-400" />}
                        </div>
                        <p className="text-white font-semibold text-sm truncate">{goal.name}</p>
                        <p className="text-xs text-slate-400 mt-1">{REALM_NAMES[realm]}</p>
                        <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full transition-all"
                            style={{ 
                              width: `${(goal.litStars / goal.stars.length) * 100}%`,
                              backgroundColor: goal.isComplete ? '#F59E0B' : REALM_COLORS[realm]
                            }}
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{goal.litStars}/{goal.stars.length} stars</p>
                      </button>
                    ))}
                  </div>
              
              {/* Interactive Sky Canvas */}
              <div 
                className="relative w-full h-[450px] bg-slate-900/30 rounded-2xl border border-slate-800/50 overflow-hidden"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
                data-testid="sky-canvas"
              >
                {/* Goal constellation outlines (faint) */}
                {Object.entries(constellationGoals).map(([realm, goal]) => {
                  // Position each constellation in different sky regions
                  // Using actual backend room IDs
                  const positions = {
                    star_map: { offsetX: 5, offsetY: 5, scale: 0.35 },
                    radiant_will: { offsetX: 55, offsetY: 5, scale: 0.35 },
                    memory_garden: { offsetX: 5, offsetY: 50, scale: 0.35 },
                    creative_forge: { offsetX: 55, offsetY: 50, scale: 0.35 },
                    calm_cove: { offsetX: 30, offsetY: 27, scale: 0.4 }
                  };
                  const pos = positions[realm] || { offsetX: 30, offsetY: 30, scale: 0.35 };
                  
                  return (
                    <div
                      key={realm}
                      className="absolute"
                      style={{
                        left: `${pos.offsetX}%`,
                        top: `${pos.offsetY}%`,
                        width: `${pos.scale * 100}%`,
                        height: `${pos.scale * 100}%`
                      }}
                    >
                      {/* Constellation lines */}
                      <AnimatedConstellationLines
                        stars={goal.stars}
                        connections={goal.connections}
                        isComplete={goal.isComplete}
                        animate={animateNewConnection && goal.litStars > 0}
                        color={REALM_COLORS[realm]}
                      />
                      
                      {/* Goal stars (lit or faint) */}
                      {goal.stars.map((starPos, i) => {
                        const isLit = i < goal.litStars;
                        const lessonStar = learningStars.find(s => s.realm === realm && s.goalIndex === i);
                        
                        return (
                          <StarPoint
                            key={`${realm}-${i}`}
                            star={{
                              ...starPos,
                              realm,
                              id: lessonStar?.id || `goal-${realm}-${i}`,
                              lesson_title: lessonStar?.lesson_title || goal.name,
                              name: goal.name
                            }}
                            isNew={lessonStar?.id === newStarId}
                            isLit={isLit}
                            isGoalStar={true}
                            onHover={isLit ? setHoveredStar : null}
                            onClick={isLit ? handleStarClick : null}
                            celebrating={goal.isComplete && celebrating?.realm === realm}
                          />
                        );
                      })}
                      
                      {/* Constellation label */}
                      <div 
                        className={`absolute text-center transition-opacity ${goal.isComplete ? 'opacity-100' : 'opacity-40'}`}
                        style={{ bottom: '-20px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}
                      >
                        <p className="text-xs font-medium" style={{ color: REALM_COLORS[realm] }}>
                          {goal.name}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Legend */}
              <div className="flex justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-white shadow shadow-white/50" />
                  <span className="text-slate-400">Lit Star (lesson completed)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-600" />
                  <span className="text-slate-400">Goal Star (waiting)</span>
                </div>
              </div>
                </>
              )}
            </div>
          ) : activeLayer === 'family' ? (
            /* Family Sky with Family Constellation */
            <div className="space-y-6">
              {/* Family Constellation Display */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-400" />
                  Your Family Constellation
                </h3>
                <FamilyConstellationDisplay 
                  familyName={selectedChild?.name?.split(' ')[0] || familyMembers[0]?.split(' ')[0] || 'Star'}
                  memberNames={familyMembers}
                />
              </div>
              
              {/* Shared Constellations Section */}
              {constellations.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-400" />
                    Shared Constellations
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {constellations.map((constellation, i) => (
                      <button
                        key={constellation.id || i}
                        onClick={() => setSelectedConstellation(constellation)}
                        className="glass-panel p-4 rounded-xl hover:border-indigo-500/30 transition-all text-left"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Star className="w-4 h-4 text-indigo-400" />
                          <span className="text-white font-medium truncate">{constellation.name}</span>
                        </div>
                        <p className="text-xs text-slate-400">
                          by {constellation.creator_name} • {constellation.stars?.length || 0} stars
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {constellations.length === 0 && (
                <div className="text-center py-8 bg-slate-900/30 rounded-xl border border-slate-800/50">
                  <Share2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Share your personal constellations with family to see them here!</p>
                </div>
              )}
            </div>
          ) : (
            /* Community constellation grid */
            constellations.length === 0 ? (
              <div className="text-center py-16">
                <Star className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  {activeLayer === 'family' ? 'No family constellations yet' : 'The Temple Sky is forming...'}
                </h3>
                <p className="text-slate-400">
                  {activeLayer === 'family' 
                    ? 'Share your constellations with family to see them here.' 
                    : 'Be the first to share a constellation with the Temple!'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {constellations.map((constellation, i) => (
                  <button
                    key={constellation.id || i}
                    onClick={() => setSelectedConstellation(constellation)}
                    className="glass-panel p-4 rounded-xl hover:border-indigo-500/30 transition-all text-left"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-indigo-400" />
                      <span className="text-white font-medium truncate">{constellation.name}</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      {constellation.stars?.length || 0} stars
                    </p>
                  </button>
                ))}
              </div>
            )
          )}
        </div>
      </div>
      
      {/* Star tooltip */}
      {hoveredStar && <StarTooltip star={hoveredStar} position={mousePos} />}
      
      {/* Constellation info panel */}
      {selectedConstellation && !celebrating && (
        <ConstellationInfoPanel
          constellation={selectedConstellation}
          onClose={() => setSelectedConstellation(null)}
          lessonsCompleted={selectedConstellation.lessonsCompleted || selectedConstellation.litStars || 0}
          totalLessons={selectedConstellation.totalLessons || selectedConstellation.stars?.length || 0}
          remainingLessons={remainingLessonsMap[selectedConstellation.realm] || []}
        />
      )}
      
      {/* Celebration overlay */}
      {celebrating && (
        <CelebrationOverlay
          constellation={celebrating}
          onComplete={() => setCelebrating(null)}
        />
      )}
      
      {/* CSS animations */}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes twinkleBright {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        @keyframes drift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, 10px); }
        }
        @keyframes drift-slow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-15px, 15px); }
        }
        .animate-drift { animation: drift 20s ease-in-out infinite; }
        .animate-drift-slow { animation: drift-slow 30s ease-in-out infinite; }
        .animate-fade-in { animation: fadeIn 0.5s ease-out; }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default TempleSkyPage;
